"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Zap,
  Pointer,
  Box,
  Lightbulb,
  MousePointerClick,
  Medal,
  PencilLine,
  ChevronLeft,
  Download,
  VideoIcon,
  ImageIcon,
  Music,
  FileText,
  TriangleAlert,
  ArrowRight,
  CloudCheck,
  CloudOff,
  Eye,
  EyeOff,
  GripVertical,
  LockKeyhole,
  Clock3Icon,
  ClockFading,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PublishConfirmModal } from "@/components/dialogs/tenants/experiences/PublishConfirmModal";
import { PublishSuccessModal } from "@/components/dialogs/tenants/experiences/PublishSuccessModal";
import {
  discoverIcon,
  fileIcon,
  movesIcon,
  Success_Icon,
  tapIcon,
  Theme_logo,
} from "@/lib/Images";
import {
  useGetSimulationConfigurationsByIdQuery,
  usePublishSimulationConfigurationsMutation,
  useUpdateSimulationConfigurationMutation,
  usePublishExperienceMutation,
  useGetSimConfigByExperienceIdQuery,
  useLazyGetSimulationConfigPresignedURLQuery,
} from "@/store/slices/apiSlice";
import { uploadToS3 } from "@/utils";
import { initAlertMsg, SHOW_MESSAGE } from "@/lib/constant/constant";
import AlertMsg from "@/components/dialogs/common/AlertMsg";
import { useDispatch, useSelector } from "react-redux";
import { getMediaFileName } from "@/lib/utils";
import { loadPrimaryColor } from "@/store/slices";

const getMaxLength = (field) => (field?.kind === "button" ? 20 : 120);

const WALL_SCREEN_UPLOAD_VIDEO_KEY = "wall_screen_upload_video";

const parseBoolean = (value) => {
  return value === true || String(value).toLowerCase() === "true";
};

const isAllowDownloadField = (field) => {
  const key = field?.key?.toLowerCase() || "";
  const id = field?.id?.toLowerCase() || "";
  const label = field?.label?.toLowerCase() || "";

  return (
    key === "allow_download" ||
    id.includes("allow_download") ||
    id.includes("download") ||
    label.includes("download")
  );
};

const getAllowDownloadValue = (field) => {
  return (
    field?.value === true ||
    field?.checked === true ||
    field?.value === "true" ||
    field?.value === "yes" ||
    field?.value === "1"
  );
};

const getStepIsDownload = (step) => {
  const allowDownloadField = step?.editableFields?.find(isAllowDownloadField);

  return getAllowDownloadValue(allowDownloadField);
};

const DEFAULT_TEMPLATE_BRAND_COLORS = [
  "#00d3f2",
  "#67d8ff",
  "#12d9ff",
  "#00d7f2",
  "#7edbff",
];

const normalizeColor = (color = "") => {
  return color?.trim?.().toLowerCase();
};

const isWhiteColor = (color = "") => {
  const value = normalizeColor(color);

  return (
    value === "#fff" ||
    value === "#ffffff" ||
    value === "white" ||
    value === "rgb(255,255,255)" ||
    value === "rgb(255, 255, 255)"
  );
};

const isTemplateBrandColor = (color = "") => {
  return DEFAULT_TEMPLATE_BRAND_COLORS.includes(normalizeColor(color));
};

const getTextColor = (field, primaryColor) => {
  if (!field) return "#FFFFFF";

  if (field.usesPrimaryColor === true) {
    return primaryColor || field.color || "#FFFFFF";
  }

  return field.color || "#FFFFFF";
};

// const getButtonBgColor = (field, primaryColor, buttonBGColor) => {
//   /* * Priority: * 1. Current/saved global buttonBGColor * 2. Existing field button color * 3. Primary color for first-time configuration */ return (
//     buttonBGColor || field?.buttonColor || primaryColor || "#00D3F2"
//   );
// };

const getButtonBgColor = (field, primaryColor) => {
  return field?.buttonBGColor || primaryColor || "#00D3F2";
};

// const HIDDEN_LEFT_STEP_IDS = ["player_interacts", "completion", "summary"];
const HIDDEN_LEFT_STEP_IDS = ["player_interacts"];

const getLeftPanelSteps = (list = []) => {
  return list.filter(
    (step) => !HIDDEN_LEFT_STEP_IDS.includes(step.id?.toLowerCase()),
  );
};

const updateStepsPriorityOrder = (list = []) => {
  return list.map((step, index) => ({
    ...step,
    step: index + 1,
    priorityOrder: index + 1,
    isVisible: step.isVisible !== false,
  }));
};

export default function ExperienceEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  // const [buttonBGColor, setButtonBGColor] = useState("");
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isPublishingExperience, setIsPublishingExperience] = useState(false);
  const [alertMsg, setAlertMsg] = useState(initAlertMsg);
  const [publishedExperienceData, setPublishedExperienceData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isStepsInitialized, setIsStepsInitialized] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [isRepublishModal, setIsRepublishModal] = useState(false);
  const [draggedStepId, setDraggedStepId] = useState("");

  const {
    tenantName,
    defaultCampaign,
    configurationId,
    templateId,
    experienceId,
    campaignId,
    simulationTemplateId,
  } = location.state || {};
  const deepLink = location.state?.deepLink;

  const globalPrimaryColor = useSelector((state) => state.global.primaryColor);

  const editorStorageKey = useMemo(() => {
    const id = configurationId || experienceId || "new";
    return `experience-editor-active-step-${id}`;
  }, [configurationId, experienceId]);

  const dispatch = useDispatch();

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!configurationId && !experienceId) {
      navigate("/tenants/profile", {
        replace: true,
      });
      return;
    }
  }, [configurationId, experienceId, navigate]);

  // console.log("first link ",  location.state)
  const isEditMode = Boolean(configurationId);
  const shouldCallConfigById = Boolean(configurationId);
  const shouldCallConfigByExperienceId =
    !configurationId && Boolean(experienceId);

  const {
    data: singleSimulationDetails,
    isLoading: isSingleSimulationLoading,
    error: singleSimulationError,
  } = useGetSimulationConfigurationsByIdQuery(
    { configurationId },
    {
      skip: !shouldCallConfigById,
    },
  );

  const {
    data: SimulationDetailsByExpId,
    isLoading: isSimulationByExpLoading,
    error: simulationByExpError,
  } = useGetSimConfigByExperienceIdQuery(
    { experienceId },
    {
      skip: !shouldCallConfigByExperienceId,
    },
  );
  const tenantId =
    singleSimulationDetails?.response?.data?.tenantId ||
    SimulationDetailsByExpId?.response?.data?.tenantId;
  const savedPrimaryColor = localStorage.getItem(
    `tenant-primary-colors-${tenantId}`,
  );

  // console.log("Global primary color", globalPrimaryColor);

  const primaryColor = globalPrimaryColor || savedPrimaryColor;
  // console.log("Global primary color", primaryColor);
  // console.log("savedPrimaryColor primary color", savedPrimaryColor);

  const [getPresignedURL] = useLazyGetSimulationConfigPresignedURLQuery();

  const [updateSimulationConfiguration] =
    useUpdateSimulationConfigurationMutation();

  const [publishSimulationConfiguration] =
    usePublishSimulationConfigurationsMutation();
  const [publishExperience] = usePublishExperienceMutation();

  const handleAlertClose = () => {
    setAlertMsg(initAlertMsg);
  };

  const getPresignedData = (response) => {
    const data =
      response?.data?.response?.data ||
      response?.response?.data ||
      response?.data ||
      response;

    const uploadUrl =
      data?.uploadUrl || data?.presignedUrl || data?.url || data?.signedUrl;

    return {
      uploadUrl,
      finalUrl: uploadUrl ? uploadUrl.split("?")[0] : "",
      key: data?.key || "",
    };
  };

  const normalizeKey = (key = "") => key.toLowerCase();

  const getElementData = (element) => {
    return element.value || element.defaults || {};
  };
  const getElementDisplayValue = (element) => {
    const data = getElementData(element);

    if (element.elementType === "BUTTON") {
      return data.buttonText || data.text || element.label || "";
    }

    if (
      element.elementType === "IMAGE" ||
      element.elementType === "VIDEO" ||
      element.elementType === "AUDIO" ||
      element.elementType === "FILE"
    ) {
      return getMediaUrl(data);
    }

    if (
      element.elementType === "BOOLEAN" ||
      element.elementType === "CHECKBOX" ||
      element.elementType === "SWITCH"
    ) {
      return getBooleanValue(data);
    }

    return data.text || data.value || "";
  };

  const getFieldKind = (elementType, data = {}) => {
    const src = getMediaUrl(data);

    if (elementType === "BUTTON") return "button";

    if (elementType === "VIDEO" || isVideoUrl(src)) return "video";
    if (elementType === "AUDIO" || isAudioUrl(src)) return "audio";
    if (elementType === "FILE" || isDocumentUrl(src)) return "file";
    if (elementType === "IMAGE") return "image";

    if (
      elementType === "BOOLEAN" ||
      elementType === "CHECKBOX" ||
      elementType === "SWITCH"
    ) {
      return "boolean";
    }

    if (elementType === "ICON") return "icon";

    return "text";
  };
  const getStepId = (screen) => {
    const key = normalizeKey(screen.key);

    if (key === "completion" || key === "summary") return "completion";
    if (key === "player_interacts") return "player_interacts";

    return key;
  };

  const getStepTitle = (screen) => {
    const key = normalizeKey(screen.key);

    if (key === "instructions") return "Instructions Screen";
    if (key === "simulations") return "SIMULATIONS";
    if (key === "player_interacts") return "PLAYER INTERACTS";
    if (key === "completion" || key === "summary") return "Summary Screen";

    return screen.title;
  };
  const getMediaUrl = (data = {}) => {
    if (typeof data === "string") return data;

    return (
      data.src ||
      data.url ||
      data.videoUrl ||
      data.mediaUrl ||
      data.fileUrl ||
      data.file ||
      data.path ||
      ""
    );
  };

  const getBooleanValue = (data = {}) => {
    if (typeof data === "boolean") return data;

    if (typeof data === "string") {
      return data === "true" || data === "yes" || data === "1";
    }

    return (
      data.value === true ||
      data.checked === true ||
      data.enabled === true ||
      data.allowDownload === true
    );
  };
  const INTERACTION_SCREEN_KEYS = [
    // "instructions",
    "turn_on_lights",
    "play_video",
    "bed_drawer",
    "passcode_lock",
    "combination_padlock",
    "lock_key",
    "reposition_chair",
    "open_wardrobe",
    "open_door",
    "square_peg",
  ];

  const getApiErrorMessage = (error, fallback = "Something went wrong.") => {
    return (
      error?.data?.response?.data?.[0] ||
      error?.data?.response?.message ||
      error?.data?.message ||
      error?.error ||
      error?.message ||
      fallback
    );
  };
  const getScreenType = (screen) => {
    const key = normalizeKey(screen.key);

    if (INTERACTION_SCREEN_KEYS.includes(key)) {
      return "AR Interaction";
    }

    return "Screen";
  };
  const mapApiScreenToStep = (screen, index) => {
    const screenStepId = getStepId(screen);

    const mappedFields = [...(screen.elements || [])]
      .sort((a, b) => a.priorityOrder - b.priorityOrder)
      .map((element) => {
        const data = getElementData(element);
        const fieldColor = data.color || "#FFFFFF";
        const label = element.label?.toLowerCase() || "";
        const key = element.key?.toLowerCase() || "";

        const isPrimaryTextByDefault =
          element.elementType !== "BUTTON" &&
          (label.includes("title") ||
            label.includes("heading") ||
            label.includes("highlight") ||
            key.includes("title") ||
            key.includes("heading") ||
            key.includes("highlight") ||
            isTemplateBrandColor(fieldColor));
        return {
          // id: element.key,
          id: `${screenStepId}__${element.key}`,
          key: element.key,
          priorityOrder: element.priorityOrder,
          label: element.label,
          kind: getFieldKind(element.elementType, data),
          elementType: element.elementType,
          value: getElementDisplayValue(element),
          // true means this text should follow global primary color
          // false means admin selected custom color, so keep field.color
          usesPrimaryColor:
            data.usesPrimaryColor === true ||
            (data.usesPrimaryColor !== false && isPrimaryTextByDefault),
          color: fieldColor,

          fontSize: data.fontSize || 14,

          // buttonColor:
          //   element.elementType === "BUTTON"
          //     ? data.buttonColor || null
          //     : undefined,
          buttonBGColor: data.buttonBGColor || data.buttonColor || primaryColor,

          src:
            element.elementType === "VIDEO" ||
            element.elementType === "IMAGE" ||
            element.elementType === "AUDIO" ||
            element.elementType === "FILE" ||
            isVideoUrl(getMediaUrl(data)) ||
            isAudioUrl(getMediaUrl(data)) ||
            isDocumentUrl(getMediaUrl(data))
              ? getMediaUrl(data)
              : null,
          linkType: data.linkType || "NONE",
          linkValue: data.linkValue || null,
        };
      });

    const hasMediaField = mappedFields.some((field) => isMediaField(field));

    const hasAllowDownloadField = mappedFields.some((field) => {
      const id = field.id?.toLowerCase() || "";
      const label = field.label?.toLowerCase() || "";

      return id.includes("download") || label.includes("download");
    });

    if (hasMediaField && !hasAllowDownloadField) {
      mappedFields.push({
        // id: "allow_download",
        id: `${screenStepId}__allow_download`,
        key: "allow_download",
        priorityOrder: 999,
        label: "Allow Download",
        kind: "boolean",
        elementType: "BOOLEAN",
        value: false,
        checked: false,
        color: "#FFFFFF",
        fontSize: 14,
        src: null,
        linkType: "NONE",
        linkValue: null,
      });
    }
    if (
      screenStepId === "play_video" &&
      !mappedFields.some((field) => field.key === WALL_SCREEN_UPLOAD_VIDEO_KEY)
    ) {
      mappedFields.push({
        id: `${screenStepId}__${WALL_SCREEN_UPLOAD_VIDEO_KEY}`,
        key: WALL_SCREEN_UPLOAD_VIDEO_KEY,
        priorityOrder: 6,
        label: "Upload Video",
        kind: "video",
        elementType: "VIDEO",
        value: "",
        color: "#FFFFFF",
        usesPrimaryColor: false,
        fontSize: 18,
        buttonColor: undefined,
        src: "",
        linkType: "NONE",
        linkValue: null,
      });
    }
    return {
      // id: getStepId(screen),
      id: screenStepId,
      title: getStepTitle(screen),
      step: index + 1,
      type: getScreenType(screen),
      icon: "zap",
      apiKey: screen.key,
      priorityOrder: screen.priorityOrder || index + 1,
      isVisible: screen.isVisible !== false,
      editableFields: mappedFields,
    };
  };

  const selectedConfiguration = useMemo(() => {
    const rawData = configurationId
      ? singleSimulationDetails?.response?.data
      : SimulationDetailsByExpId?.response?.data;

    const list = Array.isArray(rawData) ? rawData : rawData ? [rawData] : [];

    const currentTemplateId = templateId || simulationTemplateId;

    if (!currentTemplateId) {
      return list[0] || null;
    }

    return (
      list.find(
        (item) =>
          item?.simulationTemplateId === currentTemplateId ||
          item?.simulationTemplateId?._id === currentTemplateId ||
          item?._id === currentTemplateId,
      ) ||
      list[0] ||
      null
    );
  }, [
    configurationId,
    singleSimulationDetails,
    SimulationDetailsByExpId,
    templateId,
    simulationTemplateId,
  ]);

  useEffect(() => {
    if (!selectedConfiguration) return;

    const apiIsPublished =
      selectedConfiguration?.isPublished ??
      selectedConfiguration?.publishConfiguration?.isPublished ??
      selectedConfiguration?.configurationPublish?.isPublished ??
      false;

    setIsPublished(parseBoolean(apiIsPublished));
  }, [selectedConfiguration]);

  const apiScreens = useMemo(() => {
    return selectedConfiguration?.configurationJson?.screens || [];
  }, [selectedConfiguration]);

  const apiSteps = useMemo(() => {
    return [...apiScreens]
      .sort((a, b) => a.priorityOrder - b.priorityOrder)
      .map((screen, index) => mapApiScreenToStep(screen, index));
  }, [apiScreens, primaryColor]);

  const isPageLoading = isSingleSimulationLoading || isSimulationByExpLoading;

  const hasValidId = Boolean(configurationId || experienceId);

  const hasNoData =
    hasValidId &&
    !isPageLoading &&
    (!selectedConfiguration ||
      !Array.isArray(apiSteps) ||
      apiSteps.length === 0);

  const [steps, setSteps] = useState([]);

  const [selectedStepId, setSelectedStepId] = useState("");
  const [selectedFieldId, setSelectedFieldId] = useState("");

  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [experienceName, setExperienceName] = useState("");
  const [isEditingExperienceName, setIsEditingExperienceName] = useState(false);
  const [isNameInitialized, setIsNameInitialized] = useState(false);
  const [pendingPublishConfigurationId, setPendingPublishConfigurationId] =
    useState("");

  useEffect(() => {
    if (!selectedConfiguration) return;

    if (!isStepsInitialized && apiSteps.length > 0) {
      setSteps(apiSteps);

      const savedStepId = localStorage.getItem(editorStorageKey);

      const startStep =
        apiSteps.find((step) => step.id === savedStepId) ||
        apiSteps.find((step) => step.id === "instructions") ||
        apiSteps[0];

      setSelectedStepId(startStep.id);

      const firstEditable =
        startStep?.editableFields?.find((field) => field.kind !== "boolean") ||
        startStep?.editableFields?.[0];

      setSelectedFieldId(firstEditable?.id || "");

      setIsStepsInitialized(true);
      setIsDirty(false);
    }

    if (!isNameInitialized) {
      const selectedName = selectedConfiguration?.name || "";

      setExperienceName(selectedName);
      // setButtonBGColor(selectedConfiguration?.buttonBGColor || primaryColor);
      // setButtonBGColor(
      //   selectedConfiguration?.buttonBGColor || primaryColor || "",
      // );

      // first open should be Saved, not Unsaved
      setIsDirty(false);

      setIsNameInitialized(true);
    }
  }, [
    apiSteps,
    selectedConfiguration,
    isNameInitialized,
    isStepsInitialized,
    primaryColor,
    editorStorageKey,
  ]);
  useEffect(() => {
    if (selectedStepId) {
      localStorage.setItem(editorStorageKey, selectedStepId);
    }
  }, [selectedStepId, editorStorageKey]);
  const selectedStep = useMemo(
    () => steps.find((step) => step.id === selectedStepId),
    [steps, selectedStepId],
  );

  const selectedField = useMemo(
    () =>
      selectedStep?.editableFields?.find(
        (field) => field.id === selectedFieldId,
      ),
    [selectedStep, selectedFieldId],
  );

  const [experienceNameError, setExperienceNameError] = useState("");

  const updateField = (fieldId, patch) => {
    setIsDirty(true);

    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== patch.targetStepId) {
          return step;
        }

        return {
          ...step,
          editableFields: step.editableFields.map((field) => {
            if (field.id !== fieldId) {
              return field;
            }

            return {
              ...field,
              ...patch,
            };
          }),
        };
      }),
    );
  };

  const saveConfiguration = async () => {
    const finalConfigurationId = configurationId || selectedConfiguration?._id;

    if (!finalConfigurationId) {
      throw new Error("Configuration ID missing.");
    }

    const payload = buildConfigurationPayload();

    await updateSimulationConfiguration({
      configurationId: finalConfigurationId,
      body: payload,
    }).unwrap();

    setIsDirty(false);

    return finalConfigurationId;
  };

  // Auto-save when clicking another tab
  const handleSelectStep = async (stepId) => {
    const targetStep = steps.find((step) => step.id === stepId);

    if (targetStep?.isVisible === false) return;

    const isWallScreenUploadSelected =
      selectedFieldId === `${stepId}__${WALL_SCREEN_UPLOAD_VIDEO_KEY}`;

    if (stepId === selectedStepId && !isWallScreenUploadSelected) return;

    try {
      if (isDirty) {
        setIsAutoSaving(true);
        await saveConfiguration();
      }

      const nextStep = steps.find((step) => step.id === stepId);

      setSelectedStepId(stepId);
      localStorage.setItem(editorStorageKey, stepId);

      const firstEditable =
        nextStep?.editableFields?.find(
          (field) =>
            field.kind !== "boolean" &&
            field.key !== WALL_SCREEN_UPLOAD_VIDEO_KEY,
        ) || nextStep?.editableFields?.[0];

      setSelectedFieldId(firstEditable?.id || "");
    } catch (error) {
      console.error("Auto-save failed before tab change:", error);

      setAlertMsg({
        open: true,
        label: SHOW_MESSAGE?.UPDATE_SIMULATION_CONFIGURATION_FAILED_HEADING,
        msg: getApiErrorMessage(
          error,
          "Unable to save current changes before switching tab.",
        ),
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    } finally {
      setIsAutoSaving(false);
    }
  };

  const handleSelectPlayVideoUpload = async (stepId) => {
    try {
      if (isDirty) {
        setIsAutoSaving(true);
        await saveConfiguration();
      }

      setSelectedStepId(stepId);
      localStorage.setItem(editorStorageKey, stepId);
      setSelectedFieldId(`${stepId}__${WALL_SCREEN_UPLOAD_VIDEO_KEY}`);
    } catch (error) {
      console.error("Auto-save failed before upload video tab change:", error);

      setAlertMsg({
        open: true,
        label: SHOW_MESSAGE?.UPDATE_SIMULATION_CONFIGURATION_FAILED_HEADING,
        msg: getApiErrorMessage(
          error,
          "Unable to save current changes before switching tab.",
        ),
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    } finally {
      setIsAutoSaving(false);
    }
  };

  const toggleStepVisibility = (stepId) => {
    setIsDirty(true);

    setSteps((prev) =>
      prev.map((step) =>
        step.id === stepId
          ? {
              ...step,
              isVisible: step.isVisible === false,
            }
          : step,
      ),
    );
  };

  const handleDragStart = (stepId) => {
    setDraggedStepId(stepId);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (targetStepId) => {
    if (!draggedStepId || draggedStepId === targetStepId) {
      setDraggedStepId("");
      return;
    }

    setSteps((prev) => {
      const leftSteps = getLeftPanelSteps(prev);
      const hiddenSteps = prev.filter((step) =>
        HIDDEN_LEFT_STEP_IDS.includes(step.id?.toLowerCase()),
      );

      const fromIndex = leftSteps.findIndex(
        (step) => step.id === draggedStepId,
      );
      const toIndex = leftSteps.findIndex((step) => step.id === targetStepId);

      if (fromIndex === -1 || toIndex === -1) return prev;

      const firstIndex = 0;
      const lastIndex = leftSteps.length - 1;

      const draggedIsLocked =
        fromIndex === firstIndex || fromIndex === lastIndex;
      const targetIsLocked = toIndex === firstIndex || toIndex === lastIndex;

      if (draggedIsLocked || targetIsLocked) {
        return prev;
      }

      const nextLeftSteps = [...leftSteps];
      const [draggedStep] = nextLeftSteps.splice(fromIndex, 1);
      nextLeftSteps.splice(toIndex, 0, draggedStep);

      setIsDirty(true);

      return updateStepsPriorityOrder([...nextLeftSteps, ...hiddenSteps]);
    });

    setDraggedStepId("");
  };

  const handleClosePublishModals = () => {
    setShowPublishConfirm(false);
    setShowPublishSuccess(false);
    navigate("/tenants/campaigns/experiences", {
      state: {
        campaignId,
        tenantName,
        defaultCampaign,
        deepLink,
      },
    });
  };

  const getWallScreenUploadVideoElement = (step) => {
    const existingField = step?.editableFields?.find(
      (field) => field.key === WALL_SCREEN_UPLOAD_VIDEO_KEY,
    );

    return {
      key: WALL_SCREEN_UPLOAD_VIDEO_KEY,
      label: "Upload Video",
      elementType: "VIDEO",
      priorityOrder: 6,
      isDownload: false,
      defaults: {
        text: null,
        buttonText: null,
        src: existingField?.src || existingField?.value || "",
        color: "#FFFFFF",
        usesPrimaryColor: false,
        fontSize: 18,
        linkType: "NONE",
        linkValue: null,
        isDownload: false,
      },
      validations: {},
    };
  };

  // build payload to update simulation configuration API based on current steps data
  const buildConfigurationPayload = () => {
    const orderedSteps = updateStepsPriorityOrder(steps);
    const isDownload = orderedSteps.some((step) => getStepIsDownload(step));
    // const isDownload = steps.some((step) => getStepIsDownload(step));
    return {
      tenantId: selectedConfiguration?.tenantId,
      campaignId:
        selectedConfiguration?.campaignId || "6a180f3f3366f365c7911521",
      experienceId: selectedConfiguration?.experienceId,
      simulationTemplateId:
        selectedConfiguration?.simulationTemplateId || templateId,

      name: experienceName,
      // buttonBGColor,
      // buttonBGColor: buttonBGColor || primaryColor || "#00D3F2",
      isDownload,

      configurationJson: {
        templateName:
          selectedConfiguration?.configurationJson?.templateName || "",

        templateDescription:
          selectedConfiguration?.configurationJson?.templateDescription ||
          selectedConfiguration?.description ||
          "",

        screens: orderedSteps.map((step, stepIndex) => {
          const elements = step.editableFields.map((field, fieldIndex) => ({
            key: field.key || field.id,
            label: field.label,
            elementType: field.elementType,
            priorityOrder: field.priorityOrder || fieldIndex + 1,
            isDownload: getStepIsDownload(step),

            defaults: {
              text:
                field.kind === "text" || field.kind === "tip"
                  ? field.value || ""
                  : null,
              buttonText: field.kind === "button" ? field.value || "" : null,
              src:
                field.kind === "video" ||
                field.kind === "image" ||
                field.kind === "audio" ||
                field.kind === "file" ||
                field.kind === "icon"
                  ? field.src || field.value || ""
                  : null,
              // color: field.color || null,
              color:
                field.usesPrimaryColor === true
                  ? primaryColor
                  : field.color || null,

              // buttonColor:
              //   field.kind === "button"
              //     ? field.buttonColor ||
              //       buttonBGColor ||
              //       primaryColor ||
              //       "#00D3F2"
              //     : undefined,
              buttonBGColor: field.buttonBGColor || primaryColor,

              usesPrimaryColor: field.usesPrimaryColor === true,
              fontSize: field.fontSize || null,
              linkType: field.linkType || "NONE",
              linkValue: field.linkValue || null,
              value: field.kind === "boolean" ? field.value : undefined,
              checked: field.kind === "boolean" ? field.checked : undefined,
              isDownload:
                field.kind === "video" ||
                field.kind === "image" ||
                field.kind === "audio" ||
                field.kind === "file"
                  ? getStepIsDownload(step)
                  : undefined,
            },
            validations: field.validations || {},
          }));

          if (
            step.id === "play_video" &&
            !elements.some(
              (element) => element.key === WALL_SCREEN_UPLOAD_VIDEO_KEY,
            )
          ) {
            elements.push(getWallScreenUploadVideoElement(step));
          }

          return {
            key: step.apiKey || step.id,
            title: step.title,
            priorityOrder: step.priorityOrder || stepIndex + 1,
            isVisible: step.isVisible !== false,
            elements,
          };
        }),
      },
    };
  };

  // handle upload file /image/audio/video to s3 and update field src and value with uploaded url
  const handleMediaUploadToS3 = async ({ file, fieldId, stepId }) => {
    if (!file || !fieldId || !stepId) return;

    if (
      fieldId.includes(WALL_SCREEN_UPLOAD_VIDEO_KEY) &&
      !file.type.startsWith("video/")
    ) {
      setAlertMsg({
        open: true,
        label: "Upload Failed",
        msg: "Only video files are allowed for Wall Screen Upload Video.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });

      return;
    }

    try {
      const fileNameWithDate = `${Date.now()}_${file.name}`;

      const presignResponse = await getPresignedURL({
        contentType: file.type,
        fileName: fileNameWithDate,
        tenantId: selectedConfiguration?.tenantId,
      }).unwrap();

      const { uploadUrl, finalUrl } = getPresignedData(presignResponse);

      if (!uploadUrl) {
        console.error("Upload URL missing:", presignResponse);
        return;
      }

      const uploadedUrl = await uploadToS3(file, uploadUrl);
      updateField(fieldId, {
        targetStepId: stepId,
        src: uploadedUrl,
        value: uploadedUrl,
        previewSrc: `${uploadedUrl}?t=${Date.now()}`,
      });
      if (!uploadedUrl) {
        console.error("S3 upload failed");
        return;
      }

      // console.log("Uploaded finalUrl:", finalUrl);

      const isVideoFile = file.type.startsWith("video/");
      const isImageFile = file.type.startsWith("image/");
      const isAudioFile = file.type.startsWith("audio/");

      const isDocumentFile =
        file.type.includes("pdf") ||
        file.type.includes("word") ||
        file.type.includes("excel") ||
        file.type.includes("spreadsheet") ||
        file.type.includes("presentation") ||
        file.type.includes("powerpoint") ||
        file.type.includes("csv") ||
        /\.(pdf|doc|docx|xls|xlsx|csv|ppt|pptx|txt)$/i.test(file.name);

      const nextKind = isVideoFile
        ? "video"
        : isImageFile
          ? "image"
          : isAudioFile
            ? "audio"
            : isDocumentFile
              ? "file"
              : "file";

      const nextElementType = isVideoFile
        ? "VIDEO"
        : isAudioFile
          ? "AUDIO"
          : isDocumentFile
            ? "FILE"
            : "IMAGE";

      setSteps((prevSteps) =>
        prevSteps.map((step) => {
          if (step.id !== stepId) return step;

          return {
            ...step,
            editableFields: step.editableFields.map((field) => {
              if (field.id !== fieldId) return field;

              return {
                ...field,
                value: finalUrl,
                src: finalUrl,
                previewSrc: `${finalUrl}?t=${Date.now()}`,
                file: undefined,
                kind: nextKind,
                elementType: nextElementType,
                fileName: file.name,
                contentType: file.type,
              };
            }),
          };
        }),
      );
    } catch (error) {
      console.error("S3 upload failed:", error);

      setAlertMsg({
        open: true,
        label: "Upload Failed",
        msg: getApiErrorMessage(error, "File upload failed."),
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    }
  };

  const handleSaveDraft = () => {
    console.log("Save draft payload:", {
      experienceName,
      steps,
    });

    setIsEditingExperienceName(false);
  };

  // update simulation configuration API call with current step data as payload to save experience as draft without publishing
  // Publish button: update config first, then publish config
  const handlePublish = async () => {
    try {
      setIsEditingExperienceName(false);

      const wasAlreadyPublished = isPublished;

      const finalConfigurationId = await saveConfiguration();

      setIsDirty(false);

      const publishConfigRes = await publishSimulationConfiguration({
        configurationId: finalConfigurationId,
        body: {},
      }).unwrap();

      const publishConfigData =
        publishConfigRes?.response?.data ||
        publishConfigRes?.data ||
        publishConfigRes;

      const nextIsPublished = parseBoolean(publishConfigData?.isPublished);

      setIsPublished(nextIsPublished);

      // modal text depends on previous state, not new API state
      setIsRepublishModal(wasAlreadyPublished);

      setIsDirty(false);
      setPendingPublishConfigurationId(finalConfigurationId);
      setShowPublishConfirm(true);
    } catch (error) {
      console.error("Update or publish configuration failed:", error);

      setAlertMsg({
        open: true,
        label: SHOW_MESSAGE?.UPDATE_SIMULATION_CONFIGURATION_FAILED_HEADING,
        msg: getApiErrorMessage(
          error,
          "Unable to save and publish configuration. Please try again.",
        ),
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    }
  };

  // publish experience
  const confirmPublish = async () => {
    if (!experienceId) {
      console.error("Cannot publish experience: experienceId missing");
      return;
    }

    try {
      setIsPublishingExperience(true);

      // Publish experience and get latest published data from API response
      const res = await publishExperience({
        experienceId,
        body: {
          status: "PUBLISHED",
        },
      }).unwrap();

      // Normalize response data for success modal
      const publishedData = res?.response?.data || res?.data || res;

      // console.log("Published experience data:", publishedData);

      // Store published response data to show in PublishSuccessModal
      setPublishedExperienceData(publishedData);
      // Set show publish success true
      setShowPublishSuccess(true);
    } catch (error) {
      setAlertMsg({
        open: true,
        label: SHOW_MESSAGE?.PUBLISH_EXPERIENCE_FAILED_HEADING,
        msg: getApiErrorMessage(
          error,
          "Unable to publish experience. Please try again.",
        ),
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    } finally {
      setIsPublishingExperience(false);
    }
  };
  if (isPageLoading) {
    return (
      <div className="flex pt-64 items-center justify-center text-neutral-100">
        <p className="text-sm text-neutral-100/60">Loading...</p>
      </div>
    );
  }

  if (hasNoData) {
    return (
      <div className="flex pt-64 items-center justify-center text-center break-words w-full px-4 text-neutral-100">
        <h2 className="text-lg font-bold text-neutral-100/50">
          No Data Available
        </h2>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#080D18] text-neutral-100">
      <header className="flex h-12 items-center justify-between border-b border-brand-26 bg-brand-27 px-6">
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="outline"
            // onClick={() => navigate(-1)}
            // onClick={() => setShowExitConfirm(true)}
            onClick={() => {
              if (isDirty) {
                setShowExitConfirm(true);
                return;
              }
              setShowExitConfirm(true);

              // navigate(-1);
            }}
            className="h-8 border-[#3C526E] bg-transparent px-4 text-brand-24 hover:bg-white/5 hover:text-brand-24"
          >
            <ChevronLeft className="h-4 w-4" />
            Exit
          </Button>

          <div className="flex flex-col">
            <div className="flex h-8 items-center gap-3 rounded border border-[#3C526E] bg-[#0D1423] px-4">
              {isEditingExperienceName ? (
                <input
                  value={experienceName}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    const value = event.target.value;

                    if (value.length > 30) {
                      setExperienceNameError(
                        "Name should not be more than 30 characters.",
                      );
                      return;
                    }

                    setExperienceName(value);
                    setIsDirty(true);
                    setExperienceNameError("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      setIsEditingExperienceName(false);
                    }
                  }}
                  autoFocus
                  className="h-6 w-[170px] bg-transparent text-base font-semibold text-neutral-100 outline-none"
                />
              ) : (
                <span className="text-base font-semibold">
                  {experienceName}
                </span>
              )}

              {/* <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsEditingExperienceName(true);
                }}
                className="text-[#00D3F2]"
                aria-label="Edit experience name"
              >
                <PencilLine className="h-4 w-4" />
              </button> */}
            </div>

            {experienceNameError && (
              <p className="mt-1 text-xs text-red-400">{experienceNameError}</p>
            )}
          </div>

          {/* switch left of name */}
          {/* <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={isPublishSwitchOn}
              disabled={isPublishingConfig}
              onChange={handlePublishSwitchChange}
              className="hidden"
            />

            <span
              className={`relative h-5 w-10 rounded-full transition ${
                isPublishSwitchOn ? "bg-[#00D3F2]" : "bg-[#3C526E]"
              } ${isPublishingConfig ? "opacity-60" : ""}`}
            >
              <span
                className={`absolute top-1 h-3 w-3 rounded-full bg-white transition-all ${
                  isPublishSwitchOn ? "left-6" : "left-1"
                }`}
              />
            </span>
          </label> */}

          {/* <span className="rounded bg-[#232A3D] px-3 py-1 text-xs text-neutral-100/50">
            Status: In Drafts
          </span> */}
        </div>

        <div className="rounded-xl border border-[#2D3650] bg-[#111827] px-6 py-2 text-sm font-medium">
          Mobile
        </div>

        <div className="flex items-center gap-3">
          {/* <Button
            type="button"
            onClick={handleSaveDraft}
            className="h-8 bg-brand-26 px-5 text-sm text-neutral-100 hover:bg-[#2A334B]"
          >
            Save Draft
          </Button> */}
          <div className="flex items-center gap-2 text-xs text-neutral-100/50">
            {/* {isAutoSaving ? (
              <>
                <span className="h-2 w-2 animate-pulse rounded-full bg-neutral-100/50" />
                Saving...
              </>
            ) : */}
            {isDirty ? (
              <>
                <CloudOff className="h-4 w-4 text-neutral-100/50" />
                <span>Unsaved Changes </span>
              </>
            ) : (
              <>
                <CloudCheck className="h-4 w-4 text-neutral-100/50" />
                <span>Saved</span>
              </>
            )}
          </div>
          <Button
            type="button"
            onClick={handlePublish}
            className="h-8 bg-brand-28 px-5 text-sm font-bold text-black hover:bg-[#00BBD7]"
          >
            Publish
          </Button>
        </div>
      </header>

      <div className="grid h-[calc(100vh-48px)] grid-cols-[320px_1fr_320px]">
        {/* <aside className="border-r border-brand-26 bg-brand-27 px-4 py-5"> */}
        <aside className="overflow-y-auto border-r border-brand-26 bg-brand-27 px-4 py-5">
          <div className="mb-5">
            <h2 className="text-sm font-semibold text-neutral-100">
              Experience Flow
            </h2>
            <p className="mt-1 text-xs text-neutral-100/45">
              {/* {steps.length} steps in sequence */}
              {getLeftPanelSteps(steps).length}
              steps in sequence
            </p>
          </div>

          <div className="space-y-2">
            {getLeftPanelSteps(steps).map((step, index, leftSteps) => {
              const isFirst = index === 0;
              const isLast = index === leftSteps.length - 1;
              const isLocked = isFirst || isLast;
              const isHidden = step.isVisible === false;

              return (
                <div
                  key={step.id}
                  draggable={!isLocked && !isAutoSaving}
                  onDragStart={() => {
                    if (isLocked) return;
                    handleDragStart(step.id);
                  }}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(step.id)}
                >
                  <StepItem
                    step={{
                      ...step,
                      step: index + 1,
                    }}
                    selected={
                      step.id === selectedStepId &&
                      selectedFieldId !==
                        `${step.id}__${WALL_SCREEN_UPLOAD_VIDEO_KEY}`
                    }
                    disabled={isAutoSaving || isHidden}
                    isHidden={isHidden}
                    isLocked={isLocked}
                    onClick={() => handleSelectStep(step.id)}
                    onToggleVisible={() => toggleStepVisibility(step.id)}
                  />

                  {step.id === "play_video" && !isHidden && (
                    <button
                      type="button"
                      disabled={isAutoSaving}
                      onClick={() => handleSelectPlayVideoUpload(step.id)}
                      className={`mt-2 ml-8 flex w-[calc(100%-32px)] items-start gap-2 rounded-md border px-3 py-2 text-left transition ${
                        selectedStepId === step.id &&
                        selectedFieldId ===
                          `${step.id}__${WALL_SCREEN_UPLOAD_VIDEO_KEY}`
                          ? "border-brand-28 bg-[#10364A] text-neutral-100"
                          : "border-[#232B40] bg-[#181F32] text-neutral-100/80 hover:border-[#315A78]"
                      } ${isAutoSaving ? "cursor-not-allowed opacity-60" : ""}`}
                    >
                      <span className="mt-1 text-neutral-100/60">↳</span>

                      <span className="flex-1">
                        <span className="mb-1 flex items-center gap-2 text-[10px] font-semibold text-neutral-100/45">
                          Wall Screen Video
                          <span className="rounded bg-[#FF69001A] px-1.5 py-0.5 text-[10px] text-[#FF8904]">
                            Media
                          </span>
                        </span>

                        <span className="block text-xs font-bold text-neutral-100">
                          Upload Video
                        </span>
                      </span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <main className="relative overflow-auto bg-[#090E19] px-8 py-5">
          <div className="mb-4 flex items-center gap-2 text-xs text-neutral-100/50">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            Live Preview
          </div>
          {selectedStep?.id === "instructions" ? (
            <MobilePreview
              step={selectedStep}
              selectedFieldId={selectedFieldId}
              onSelectField={setSelectedFieldId}
              // buttonBGColor={buttonBGColor}
              primaryColor={primaryColor}
            />
          ) : selectedStep?.id === "completion" ? (
            <CompletionPreview
              step={selectedStep}
              selectedFieldId={selectedFieldId}
              onSelectField={setSelectedFieldId}
              // buttonBGColor={buttonBGColor}
              primaryColor={primaryColor}
            />
          ) : (
            <InteractionVideoEditor
              step={selectedStep}
              selectedFieldId={selectedFieldId}
              onSelectField={setSelectedFieldId}
              primaryColor={primaryColor}
            />
          )}
        </main>

        <aside className="border-l border-brand-26 bg-brand-27">
          <PropertiesPanel
            step={selectedStep}
            field={selectedField}
            // onChange={(patch) =>
            //   updateField(patch.targetFieldId || selectedFieldId, patch)
            // }
            onChange={(patch) =>
              updateField(patch.targetFieldId || selectedFieldId, {
                ...patch,
                targetStepId: patch.targetStepId || selectedStep?.id,
              })
            }
            onMediaUpload={handleMediaUploadToS3}
            // buttonBGColor={buttonBGColor}
            // setButtonBGColor={setButtonBGColor}
            primaryColor={primaryColor}
          />
        </aside>
      </div>
      <PublishConfirmModal
        open={showPublishConfirm}
        onClose={() => {
          setShowPublishConfirm(false);
          // navigate("/tenants/campaigns/experiences", {
          //   state: {
          //     campaignId,
          //   },
          // });
        }}
        onPublish={confirmPublish}
        isRepublish={isRepublishModal}
      />

      <PublishSuccessModal
        open={showPublishSuccess}
        onClose={handleClosePublishModals}
        experienceName={publishedExperienceData?.name || experienceName}
        campaignName={publishedExperienceData?.campaignName || defaultCampaign}
        tenantName={publishedExperienceData?.tenantName || tenantName}
        inviteLink={
          publishedExperienceData?.deepLink ||
          deepLink ||
          "https://shadowcast.chottu.link/Channel4"
        }
      />
      {showExitConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70">
          <div className="w-[420px] rounded-lg border border-[#00D3F2]/40 bg-[#132033] px-8 py-8 text-center shadow-2xl">
            <div className="mx-auto mb-5 flex h-10 w-10 items-center justify-center rounded-full text-brand-116">
              <TriangleAlert className="h-12 w-12 fill-red-800" />
            </div>

            <h2 className="mb-3 text-lg font-bold text-[#67D8FF]">
              Exit without saving?
            </h2>

            <p className="mx-auto mb-6 max-w-[330px] text-xs leading-relaxed text-neutral-100/80">
              You have unsaved changes. If you exit now, your recent edits will
              be lost. You can save your progress as a draft before leaving.
            </p>

            <div className="flex justify-center gap-3">
              <Button
                type="button"
                variant="neutral"
                onClick={() => setShowExitConfirm(false)}
                className="h-9 border-[#607A9B] bg-transparent px-6 text-xs text-neutral-100 hover:bg-white/5"
              >
                Go Back
              </Button>

              <Button
                type="button"
                onClick={() => {
                  setShowExitConfirm(false);
                  navigate(-1);
                }}
                className="h-9 bg-[#168FE3] px-6 text-xs font-bold text-white hover:bg-[#0B7DCC]"
              >
                Exit without publishing
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </div>
  );
}

const StepItem = ({
  step,
  selected,
  onClick,
  disabled,
  isHidden,
  isLocked,
  onToggleVisible,
}) => {
  return (
    <div
      className={`w-full rounded-lg border px-3 py-3 text-left transition ${
        isHidden ? "opacity-75" : ""
      } ${
        selected && !isHidden
          ? "border-brand-28 bg-[#10364A]"
          : "border-[#232B40] bg-[#181F32] hover:border-[#315A78]"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="">
          {isLocked ? (
            <LockKeyhole className="h-4 w-4 text-white" strokeWidth={2} />
          ) : (
            <GripVertical
              className="h-4 w-4 cursor-grab text-white"
              title="Drag to reorder"
              strokeWidth={2}
            />
          )}
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className="flex flex-1 gap-3 text-left disabled:cursor-not-allowed"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#101827]">
            <Zap className="h-4 w-4 text-brand-23" />
          </div>

          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs text-neutral-100/45">
                Step {step.step}
              </span>

              <span className="rounded bg-brand-25 px-1.5 py-0.5 text-[10px] text-brand-24">
                {step.type}
              </span>
            </div>

            <p className="truncate text-xs font-bold text-neutral-100">
              {step.title}
            </p>
          </div>
        </button>
        {step.id !== "instructions" && step.id !== "completion" && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleVisible();
            }}
            className={`flex h-7 w-7 items-center justify-center rounded-md  transition `}
            title={isHidden ? "Enable screen" : "Disable screen"}
          >
            {isHidden ? (
              <EyeOff className="h-4 w-4 text-white" strokeWidth={2} />
            ) : (
              <Eye className="h-4 w-4 text-white" strokeWidth={3} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const MobilePreview = ({
  step,
  selectedFieldId,
  onSelectField,
  // buttonBGColor,
  primaryColor,
}) => {
  if (!step) {
    return (
      <div className="flex justify-center pt-10 text-sm text-neutral-100/50">
        {/* Loading preview... */}
        No Data Available
      </div>
    );
  }

  return (
    <div className="flex justify-center pt-6 pb-10">
      <div className="w-[360px] rounded-[28px] border-[8px] border-[#344768] bg-[#07070B] px-4 py-10">
        {step.id === "splash" && (
          <SplashPreview
            step={step}
            selectedFieldId={selectedFieldId}
            onSelectField={onSelectField}
          />
        )}

        {step.id === "welcome" && (
          <WelcomePreview
            step={step}
            selectedFieldId={selectedFieldId}
            onSelectField={onSelectField}
            primaryColor={primaryColor}
          />
        )}

        {step.id === "kickoff" && (
          <KickoffPreview
            step={step}
            selectedFieldId={selectedFieldId}
            onSelectField={onSelectField}
          />
        )}

        {step.id === "instructions" && (
          <InstructionsPreview
            step={step}
            selectedFieldId={selectedFieldId}
            onSelectField={onSelectField}
            // buttonBGColor={buttonBGColor}
            primaryColor={primaryColor}
          />
        )}

        {![
          "splash",
          "welcome",
          "kickoff",
          "instructions",
          "play_video",
        ].includes(step.id) && (
          <DynamicScreenPreview
            step={step}
            selectedFieldId={selectedFieldId}
            onSelectField={onSelectField}
          />
        )}
      </div>
    </div>
  );
};

const DynamicScreenPreview = ({ step, selectedFieldId, onSelectField }) => {
  return (
    <div className="space-y-3">
      <h3 className="mb-5 text-center text-xs font-bold tracking-[5px] text-neutral-100">
        {step.title}
      </h3>

      {step.editableFields.map((field) => {
        if (field.kind === "image") {
          return (
            <EditablePreviewBox
              key={field.id}
              selected={selectedFieldId === field.id}
              onClick={() => onSelectField(field.id)}
              className="flex min-h-[120px] items-center justify-center text-center text-xs text-neutral-100/60"
            >
              {field.src ? (
                <img
                  src={field.previewSrc || field.src || field.value}
                  alt={field.label}
                  className="max-h-[180px] max-w-full rounded object-contain"
                />
              ) : (
                <span>{field.label}</span>
              )}
            </EditablePreviewBox>
          );
        }

        if (field.kind === "button") {
          return (
            <div key={field.id} className="flex justify-center pt-4">
              <button
                type="button"
                onClick={() => onSelectField(field.id)}
                className={`rounded-xl px-7 break-words py-3 text-sm font-bold ${
                  selectedFieldId === field.id ? "ring-2 ring-[#DFFF00]" : ""
                }`}
                style={{
                  // backgroundColor: field.buttonColor || "#12D9FF",
                  backgroundColor:
                    field.buttonColor ||
                    // buttonBGColor ||
                    primaryColor ||
                    "#00D3F2",
                  color: field.color || "#000000",
                  fontSize: field.fontSize,
                }}
              >
                {/* {field.value} */}
                <span>{field.value}</span>
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={3} />
              </button>
            </div>
          );
        }

        return (
          <EditablePreviewBox
            key={field.id}
            selected={selectedFieldId === field.id}
            onClick={() => onSelectField(field.id)}
            className="px-3 py-3 text-center text-sm leading-relaxed"
            style={{
              color: field.color,
              fontSize: field.fontSize,
            }}
          >
            {field.value}
          </EditablePreviewBox>
        );
      })}
    </div>
  );
};
const isVideoUrl = (url = "") => {
  if (!url) return false;

  return /\.(mp4|webm|ogg|mov)$/i.test(url);
};
const isImageUrl = (url = "") => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url);
};
const isAudioUrl = (url = "") => {
  if (!url) return false;
  return /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(url);
};
const isDocumentUrl = (url = "") => {
  if (!url) return false;

  return /\.(pdf|doc|docx|xls|xlsx|csv|ppt|pptx|txt)$/i.test(url);
};
const getFileExtension = (url = "") => {
  const cleanUrl = url.split("?")[0];
  return cleanUrl.split(".").pop()?.toUpperCase() || "FILE";
};

const isMediaField = (field) => {
  const mediaUrl = field?.previewSrc || field?.src || field?.value || "";
  const id = field?.id?.toLowerCase() || "";
  const label = field?.label?.toLowerCase() || "";

  if (id.includes("download") || label.includes("download")) {
    return false;
  }

  return (
    field?.kind === "video" ||
    field?.kind === "image" ||
    field?.kind === "audio" ||
    field?.kind === "file" ||
    field?.elementType === "VIDEO" ||
    field?.elementType === "IMAGE" ||
    field?.elementType === "AUDIO" ||
    field?.elementType === "FILE" ||
    isVideoUrl(mediaUrl) ||
    isImageUrl(mediaUrl) ||
    isAudioUrl(mediaUrl) ||
    isDocumentUrl(mediaUrl) ||
    id.includes("media") ||
    label.includes("media") ||
    id.includes("video") ||
    label.includes("video") ||
    id.includes("image") ||
    label.includes("image") ||
    id.includes("audio") ||
    label.includes("audio") ||
    id.includes("file") ||
    label.includes("file") ||
    id.includes("document") ||
    label.includes("document") ||
    field?.id === "instruction_view"
  );
};

const getMediaType = (field) => {
  // const mediaUrl = field?.src || field?.value || "";
  const mediaUrl = field?.previewSrc || field?.src || field?.value || "";

  if (field?.elementType === "VIDEO" || field?.kind === "video") return "video";
  if (field?.elementType === "AUDIO" || field?.kind === "audio") return "audio";
  if (field?.elementType === "FILE" || field?.kind === "file") return "file";

  if (field?.elementType === "IMAGE" || field?.kind === "image") {
    if (isVideoUrl(mediaUrl)) return "video";
    if (isAudioUrl(mediaUrl)) return "audio";
    if (isDocumentUrl(mediaUrl)) return "file";
    return "image";
  }

  if (isVideoUrl(mediaUrl)) return "video";
  if (isImageUrl(mediaUrl)) return "image";
  if (isAudioUrl(mediaUrl)) return "audio";
  if (isDocumentUrl(mediaUrl)) return "file";

  return "image";
};
const getMediaUploadMeta = (field) => {
  const mediaType = getMediaType(field);
  const hasMedia = Boolean(field?.src || field?.value);

  const defaultMeta = {
    iconSrc: fileIcon,
    // title: hasMedia ? "Change Asset" : "Upload Asset",
    title: "Upload Asset",
    hint: "You can Upload image, video, audio, word, pdf, excel, csv, ppt and others",
    accept:
      "image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,audio/mpeg,audio/wav,audio/mp3,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt",
  };

  // if (mediaType === "image") {
  //   return {
  //     ...defaultMeta,
  //     title: hasMedia ? "Change Image" : "Upload Asset",
  //     Icon: hasMedia ? ImageIcon : "",
  //   };
  // }

  // if (mediaType === "video") {
  //   return {
  //     ...defaultMeta,
  //     title: hasMedia ? "Change Video" : "Upload Asset",
  //     Icon: hasMedia ? VideoIcon : "",
  //   };
  // }

  // if (mediaType === "audio") {
  //   return {
  //     ...defaultMeta,
  //     title: hasMedia ? "Change Audio" : "Upload Asset",
  //     Icon: hasMedia ? Music : "",
  //   };
  // }

  // if (mediaType === "file") {
  //   return {
  //     ...defaultMeta,
  //     title: hasMedia ? "Change File" : "Upload Asset",
  //     Icon: hasMedia ? FileText : "",
  //   };
  // }

  return defaultMeta;
};
const INTERACTION_DETAILS = {
  play_video: {
    object: "Tap to play 📺",
    objectDescription: "Wall-mounted keypad with numeric buttons",
    logic: "Press button",
    action: "User presses video",
    trigger: "Button Press",
    reward: "Video will play on the screen",
  },

  turn_on_lights: {
    object: "Light Switch 💡",
    objectDescription: "",
    logic: "Toggle switch",
    action: "User taps the switch",
    trigger: "Switch activated/turned ON",
    reward: "Room lights turn on and hidden objects become visible",
  },
  bed_drawer: {
    object: "Bedside Drawer  🗄️",
    objectDescription: "",
    logic: "Drag to open",
    action: "User pull drawer outward",
    trigger: "Drawer fully opened",
    reward: "Hidden clue/code is revealed ",
  },

  passcode_lock: {
    object: "Passcode Panel ⌨️",
    objectDescription: "",
    logic: "Code Entry (Numeric Input)",
    action: "User inputs code using keypad",
    trigger: "Correct 4-digit code entered",
    reward: "Unlocks and reveals key item",
  },

  combination_padlock: {
    object: "Combination Padlock 🔒",
    objectDescription: "",
    logic: "Drag to open",
    action: "User aligns correct number sequence",
    trigger: "Correct combination aligned",
    reward: "Padlock opens revealing hidden object",
  },
  lock_key: {
    object: "Lock & Key 🔐",
    objectDescription: "",
    logic: "Drag and insert key",
    action: "User moves key toward the lock",
    trigger: "Key inserted successfully",
    reward: "Lock mechanism opens and reveals",
  },
  reposition_chair: {
    object: "Wooden Chair 🪑",
    objectDescription: "",
    logic: "Drag object to target area",
    action: "User moves chair to marked position",
    trigger: "Chair placed correctly",
    reward: "Hidden path/clue becomes visible",
  },
  open_wardrobe: {
    object: "Wardrobe 🗄️",
    objectDescription: "",
    logic: "Open wardrobe doors",
    action: "User taps or drags the wardrobe doors open",
    trigger: "Wardrobe fully opened",
    reward: "Hidden clue, key item or reward is revealed",
  },
  open_door: {
    object: "Exit Door 🚪",
    objectDescription: "",
    logic: "Pull/Open door",
    action: "User interacts with door handle",
    trigger: "Door unlocked",
    reward: "Room completed / mission successful",
  },
  square_peg: {
    object: "Square Peg and Block 🧩",
    objectDescription: "",
    logic: "Pull/Open door",
    action: "User interacts with door handle",
    trigger: "Door unlocked",
    reward: "Room completed / mission successful",
  },
};
const getDynamicInteractionDetails = (step) => {
  const key = step?.id || step?.apiKey;

  return INTERACTION_DETAILS[key];
};

const CompletionPreview = ({
  step,
  selectedFieldId,
  onSelectField,
  // buttonBGColor,
  primaryColor,
}) => {
  const fields = [...(step?.editableFields || [])].sort(
    (a, b) => (a.priorityOrder || 0) - (b.priorityOrder || 0),
  );

  return (
    <div className="flex justify-center pt-6 pb-10">
      <div className="flex min-h-[560px] w-[360px] flex-col items-center justify-center rounded-[28px] border-[8px] border-[#344768] bg-[#07070B] px-5 py-10">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/15">
          <img src={Success_Icon} className="h-14 w-14" title="Success Icon" />
        </div>

        {fields.map((field) => {
          const isTimeTaken =
            field.key === "completion_time_taken" ||
            field.label === "Time Taken";
          if (field.kind === "button") {
            return (
              <button
                key={field.id}
                type="button"
                onClick={() => onSelectField(field.id)}
                className={`mt-5 min-w-[170px] rounded-xl px-6 py-4 text-sm font-bold ${
                  selectedFieldId === field.id ? "ring-2 ring-[#DFFF00]" : ""
                }`}
                style={{
                  // backgroundColor:
                  //   field.buttonColor ||
                  //   buttonBGColor ||
                  //   primaryColor ||
                  //   "#00D3F2",
                  backgroundColor:
                    field.buttonBGColor || primaryColor || "#00D3F2",
                  color: field.color || "#000000",
                  fontSize: field.fontSize || 16,
                }}
              >
                {field.value}
              </button>
            );
          }

          if (field.kind === "image") {
            return (
              <EditablePreviewBox
                key={field.id}
                selected={selectedFieldId === field.id}
                onClick={() => onSelectField(field.id)}
                className="mb-4 flex min-h-[120px] items-center justify-center"
              >
                {field.src || field.value ? (
                  <img
                    src={field.previewSrc || field.src || field.value}
                    alt={field.label || "Completion"}
                    className="max-h-[180px] max-w-full rounded object-contain"
                  />
                ) : (
                  <span className="text-xs text-neutral-100/50">
                    {field.label}
                  </span>
                )}
              </EditablePreviewBox>
            );
          }

          if (field.kind === "video") {
            return (
              <EditablePreviewBox
                key={field.id}
                selected={selectedFieldId === field.id}
                onClick={() => onSelectField(field.id)}
                className="mb-4 flex min-h-[120px] items-center justify-center"
              >
                {field.src || field.value ? (
                  <video
                    src={field.previewSrc || field.src || field.value}
                    controls
                    className="max-h-[180px] max-w-full rounded"
                  />
                ) : (
                  <span className="text-xs text-neutral-100/50">
                    {field.label}
                  </span>
                )}
              </EditablePreviewBox>
            );
          }

          return (
            <EditablePreviewBox
              key={field.id}
              selected={!isTimeTaken && selectedFieldId === field.id}
              onClick={isTimeTaken ? () => {} : () => onSelectField(field.id)}
              className={`mb-3 px-3 py-3 text-center ${
                isTimeTaken ? "cursor-default border-none" : ""
              }`}
              style={{
                color: getTextColor(field, primaryColor),
                fontSize: field.fontSize || 14,
              }}
            >
              {isTimeTaken ? (
                <div className="flex items-start justify-start gap-5 border border-white/25 p-3 rounded-md">
                  <ClockFading className="h-5 w-5" />
                  <span className="text-base">{field.value} : </span>
                </div>
              ) : (
                field.value
              )}
            </EditablePreviewBox>
          );
        })}
      </div>
    </div>
  );
};

const InteractionVideoEditor = ({
  step,
  selectedFieldId,
  onSelectField,
  primaryColor,
}) => {
  if (!step) {
    return (
      <div className="flex min-h-[calc(100vh-120px)] items-center justify-center text-sm text-neutral-100/60">
        Loading interaction details...
      </div>
    );
  }

  const fields = [...(step?.editableFields || [])].sort(
    (a, b) => a.priorityOrder - b.priorityOrder,
  );

  const selectedField = fields.find((field) => field.id === selectedFieldId);

  const isWallScreenUploadVideoSelected =
    selectedField?.key === WALL_SCREEN_UPLOAD_VIDEO_KEY;

  const details = getDynamicInteractionDetails(step);

  const mediaField = fields.find(isMediaField);
  const mediaType = getMediaType(mediaField);
  const mediaUrl =
    mediaField?.previewSrc ||
    mediaField?.src ||
    mediaField?.value ||
    mediaField?.url ||
    mediaField?.mediaUrl ||
    mediaField?.fileUrl ||
    "";

  const selectedUploadVideoUrl =
    selectedField?.previewSrc ||
    selectedField?.src ||
    selectedField?.value ||
    "";

  const bottomTextFields = fields.filter((field) => {
    const key = field.key?.toLowerCase() || "";
    const label = field.label?.toLowerCase() || "";

    return (
      field.kind === "text" &&
      field.elementType !== "BUTTON" &&
      !key.includes("button") &&
      !label.includes("button")
    );
  });

  const allowDownloadField = fields.find((field) => {
    const id = field.id?.toLowerCase() || "";
    const label = field.label?.toLowerCase() || "";

    return id.includes("download") || label.includes("download");
  });

  const allowDownload =
    allowDownloadField?.value === true ||
    allowDownloadField?.value === "true" ||
    allowDownloadField?.value === "yes" ||
    allowDownloadField?.checked === true;

  const handleMediaClick = () => {
    if (mediaField?.id) {
      onSelectField(mediaField.id);
    }
  };

  if (isWallScreenUploadVideoSelected) {
    return (
      <div className="relative flex min-h-[calc(100vh-120px)] items-center justify-center">
        <div className="flex flex-col items-center">
          <h2 className="mb-4 whitespace-nowrap text-center text-sm font-extrabold uppercase text-white tracking-wide">
            PREVIEW
          </h2>

          <div className="relative h-[clamp(560px,78vh,760px)] w-full max-w-[400px] overflow-hidden rounded-[28px] border-[10px] border-[#344768] bg-[#05070D] shadow-[0_0_45px_rgba(0,211,242,0.08)] sm:max-w-[460px] lg:max-w-[380px]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(0,211,242,0.08),transparent_35%),linear-gradient(180deg,#080D18_0%,#05070D_100%)]" />

            <div className="relative z-10 flex h-full flex-col justify-center overflow-y-auto px-8 py-10">
              <button
                type="button"
                onClick={() =>
                  selectedField?.id && onSelectField(selectedField.id)
                }
                className={`mx-auto flex aspect-[4/3] w-full max-w-[300px] items-center justify-center border-[5px] bg-[#A6A6A6] px-4 text-center text-sm font-bold uppercase tracking-wide text-white ${
                  selectedFieldId === selectedField?.id
                    ? "border-[#DFFF00]"
                    : "border-transparent"
                }`}
              >
                {selectedUploadVideoUrl ? (
                  <video
                    key={selectedUploadVideoUrl}
                    controls
                    controlsList="nodownload"
                    className="h-full w-full object-contain"
                  >
                    <source src={selectedUploadVideoUrl} type="video/mp4" />
                    <source src={selectedUploadVideoUrl} type="video/webm" />
                    <source src={selectedUploadVideoUrl} type="video/ogg" />
                  </video>
                ) : (
                  "Upload the video that will play on this wall-mounted screen when the user completes the interaction."
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[calc(100vh-120px)] items-center justify-center gap-[110px]">
      <div className="relative">
        <h3 className="mb-3 text-sm font-bold tracking-wide">
          INTERACTION DETAILS{" "}
          <span className="text-xs font-normal text-neutral-100/40">
            (READ ONLY)
          </span>
        </h3>

        <div className="w-[370px] rounded-xl border-[5px] border-white/35 bg-[#171E31] p-5">
          <InteractionDetailRow
            icon={Box}
            label="Object"
            title={details.object}
            description={details.objectDescription}
          />

          <InteractionDetailRow
            icon={Lightbulb}
            label="Interaction Logic"
            title={details.logic}
          />

          <InteractionDetailRow
            icon={MousePointerClick}
            label="User Action"
            title={details.action}
          />

          <InteractionDetailRow
            icon={Zap}
            label="Trigger Condition"
            title={details.trigger}
          />

          <InteractionDetailRow
            icon={Medal}
            label="Outcome / Reward"
            title={details.reward}
            last
          />
        </div>

        <div className="absolute left-full top-[155px] hidden h-[80px] w-[125px] lg:block">
          <div className="absolute left-[-13px] top-[32px] flex h-6 w-6 items-center justify-center rounded-full bg-[#53565E]">
            <div className="h-2.5 w-2.5 rounded-full bg-white shadow-[0_0_6px_rgba(255,255,255,0.4)]" />
          </div>

          <div className="absolute left-[6px] top-[40px] h-[4px] w-[34px] rounded-full bg-[#53565E]" />
          <div className="absolute left-[36px] top-[12px] h-[32px] w-[4px] rounded-full bg-[#53565E]" />
          <div className="absolute left-[36px] top-[12px] h-[4px] w-[68px] rounded-full bg-[#53565E]" />

          <div
            className="absolute left-[100px] top-[6px] h-0 w-0"
            style={{
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderLeft: "12px solid #53565E",
            }}
          />
        </div>
      </div>
      <div className="flex flex-col items-center">
        <h2 className="mb-4 whitespace-nowrap text-center text-sm font-extrabold uppercase text-white tracking-wide">
          INTERACTION VIEW/OVERLAY
        </h2>

        <div className="relative h-[clamp(560px,78vh,760px)] w-full max-w-[400px] overflow-hidden rounded-[28px] border-[10px] border-[#344768] bg-[#05070D] shadow-[0_0_45px_rgba(0,211,242,0.08)] sm:max-w-[460px] lg:max-w-[380px]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(0,211,242,0.08),transparent_35%),linear-gradient(180deg,#080D18_0%,#05070D_100%)]" />

          <div className="relative z-10 flex h-full flex-col justify-center overflow-y-auto px-8 py-10">
            <div className="mx-auto w-full max-w-[330px] rounded-lg border border-[#00D3F2]/80 bg-[#05070D]/80 p-2 shadow-[0_0_18px_rgba(0,211,242,0.28)] sm:max-w-[360px]">
              <div className="relative border border-dashed border-white/35 p-1">
                <button
                  type="button"
                  onClick={handleMediaClick}
                  className={`relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden border-[5px] bg-[#A6A6A6] transition-all ${
                    selectedFieldId === mediaField?.id
                      ? "border-[#DFFF00]"
                      : "border-transparent"
                  }`}
                >
                  {mediaUrl ? (
                    mediaType === "video" ? (
                      <video
                        key={mediaUrl}
                        controls
                        controlsList="nodownload"
                        className="h-full w-full object-contain"
                      >
                        <source src={mediaUrl} type="video/mp4" />
                        <source src={mediaUrl} type="video/webm" />
                        <source src={mediaUrl} type="video/ogg" />
                      </video>
                    ) : mediaType === "audio" ? (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#111827] px-3 text-center">
                        <Music className="h-10 w-10 text-[#00D3F2]" />
                        <p className="text-xs font-bold text-white">
                          {mediaField?.fileName || "Audio File"}
                        </p>
                        <audio
                          controls
                          controlsList="nodownload"
                          className="w-full"
                        >
                          <source src={mediaUrl} />
                        </audio>
                      </div>
                    ) : mediaType === "file" ? (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[#111827] px-3 text-center">
                        <FileText className="h-12 w-12 text-[#00D3F2]" />
                        <p className="text-xs font-bold text-white">
                          {mediaField?.fileName ||
                            `${getFileExtension(mediaUrl)} File`}
                        </p>
                      </div>
                    ) : (
                      <img
                        src={mediaUrl}
                        alt={mediaField?.label || "media"}
                        className="h-full w-full object-contain"
                      />
                    )
                  ) : (
                    <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm font-bold uppercase tracking-wide text-white">
                      MEDIA ASSETS WILL SHOW HERE
                    </div>
                  )}
                </button>

                {allowDownload && mediaUrl && (
                  <button
                    type="button"
                    onClick={async (event) => {
                      event.stopPropagation();
                      event.preventDefault();

                      try {
                        const cleanUrl = mediaUrl.split("?")[0];

                        const response = await fetch(cleanUrl, {
                          mode: "cors",
                        });

                        if (!response.ok) {
                          throw new Error("Download failed");
                        }

                        const blob = await response.blob();
                        const blobUrl = window.URL.createObjectURL(blob);

                        const extension =
                          mediaField?.fileName?.split(".").pop() ||
                          getFileExtension(cleanUrl).toLowerCase();

                        const fileName =
                          mediaField?.fileName ||
                          `download-${mediaType}.${extension}`;

                        const link = document.createElement("a");
                        link.href = blobUrl;
                        link.download = fileName;
                        link.style.display = "none";

                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        window.URL.revokeObjectURL(blobUrl);
                      } catch (error) {
                        console.error("Download failed:", error);
                      }
                    }}
                    className="absolute bottom-3 right-3 z-30 rounded-full bg-[#547386]/90 p-2 shadow-[0_0_12px_rgba(0,211,242,0.35)]"
                    title={`Download ${mediaType}`}
                  >
                    <Download className="h-4 w-4 text-white" />
                  </button>
                )}
              </div>

              {bottomTextFields.length > 0 && (
                <div className="mt-2 space-y-2 text-center">
                  {/* {bottomTextFields.map((field, index) => {
                    const isTitle =
                      index === 0 ||
                      field.label?.toLowerCase().includes("title") ||
                      field.key?.toLowerCase().includes("title");

                    return (
                      <button
                        key={field.id}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelectField(field.id);
                        }}
                        className={`block w-full whitespace-normal break-words text-center transition ${
                          isTitle
                            ? "border border-dashed border-white/50 px-4 py-3"
                            : "border border-dashed border-white/30 px-3 py-2"
                        } ${
                          selectedFieldId === field.id
                            ? "ring-2 ring-[#DFFF00]"
                            : ""
                        }`}
                        // style={{
                        //   color: isTitle ? "#00D3F2" : field.color || "#FFFFFF",
                        //   fontSize: isTitle ? 17 : 12,
                        //   lineHeight: isTitle ? "1.25" : "1.45",
                        //   fontWeight: isTitle ? 800 : 400,
                        // }}
                        style={{
                          color: isTitle
                            ? primaryColor
                            : getTextColor(field, primaryColor),
                          fontSize: isTitle ? 17 : 12,
                          lineHeight: isTitle ? "1.25" : "1.45",
                          fontWeight: isTitle ? 800 : 400,
                        }}
                      >
                        {field.value || field.label}
                      </button>
                    );
                  })} */}
                  {bottomTextFields.map((field, index) => {
                    const label = field.label?.toLowerCase() || "";
                    const key = field.key?.toLowerCase() || "";

                    const isTitle =
                      index === 0 ||
                      label.includes("title") ||
                      label.includes("heading") ||
                      key.includes("title") ||
                      key.includes("heading");

                    const isHighlight =
                      label.includes("highlight") || key.includes("highlight");

                    return (
                      <button
                        key={field.id}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelectField(field.id);
                        }}
                        className={`block w-full whitespace-normal break-words text-center transition ${
                          isTitle || isHighlight
                            ? "border border-dashed border-white/50 px-4 py-3"
                            : "border border-dashed border-white/30 px-3 py-2"
                        } ${
                          selectedFieldId === field.id
                            ? "ring-2 ring-[#DFFF00]"
                            : ""
                        }`}
                        style={{
                          color: getTextColor(field, primaryColor),
                          fontSize: isTitle ? 17 : 12,
                          lineHeight: isTitle ? "1.25" : "1.45",
                          fontWeight: isTitle ? 800 : 400,
                        }}
                      >
                        {field.value || field.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InteractionDetailRow = ({
  icon: Icon,
  label,
  title,
  description,
  last = false,
}) => {
  return (
    <div
      className={`flex gap-4 py-3 ${last ? "" : "border-b border-white/10"}`}
    >
      <Icon className="mt-1 h-5 w-5 shrink-0 text-brand-28" />

      <div>
        <p className="text-sm text-neutral-100/50">{label}</p>
        <p className="text-base text-neutral-100">{title}</p>

        {description && (
          <p className="text-sm text-neutral-100/45">{description}</p>
        )}
      </div>
    </div>
  );
};

const getField = (step, key) =>
  [...(step?.editableFields || [])]
    .sort((a, b) => a.priorityOrder - b.priorityOrder)
    .find(
      (item) =>
        item.id === key || item.key === key || item.priorityOrder === key,
    );
// .find((item) => item.id === key || item.priorityOrder === key);

const SplashPreview = ({ step, selectedFieldId, onSelectField }) => {
  const tagline = getField(step, "splashTagline");
  const loadingText = getField(step, "splashLoadingText");

  return (
    <div className="flex min-h-[560px] flex-col items-center justify-center">
      <div className="mb-10 text-center">
        {/* <div className="mx-auto mb-3 h-12 w-12 rounded bg-gradient-to-br from-brabg-brand-28 to-[#7C2AFF]" />
        <h3 className="text-xl font-bold text-brabg-brand-28">
          SIMSTAC
          <span className="ml-1 text-purple-500">SYSTEMS</span>
        </h3> */}
        <div className="flex justify-center">
          <img
            src={Theme_logo || "/placeholder.svg"}
            className="h-20"
            alt="Simstac Systems Logo"
          />
        </div>
      </div>

      <EditablePreviewBox
        selected={selectedFieldId === tagline?.id}
        onClick={() => onSelectField(tagline.id)}
        className="px-4 py-3 text-center text-sm tracking-[5px]"
        style={{ color: tagline?.color }}
      >
        {tagline?.value}
      </EditablePreviewBox>

      <div className="mt-24 w-full">
        <div className="mx-auto mb-3 h-1 w-28 rounded bg-gradient-to-r from-brabg-brand-28 to-[#7C2AFF]" />

        <EditablePreviewBox
          selected={selectedFieldId === loadingText?.id}
          onClick={() => onSelectField(loadingText.id)}
          className="py-2 text-center text-xs"
          style={{ color: loadingText?.color }}
        >
          {loadingText?.value}
        </EditablePreviewBox>
      </div>
    </div>
  );
};

const WelcomePreview = ({
  step,
  selectedFieldId,
  onSelectField,
  primaryColor,
}) => {
  const fields = [...(step.editableFields || [])].sort(
    (a, b) => a.priorityOrder - b.priorityOrder,
  );

  return (
    <div className="w-full min-h-[560px]">
      {fields.map((item) => {
        // BUTTON
        if (item.kind === "button") {
          return (
            <EditablePreviewBox
              key={item.id}
              selected={selectedFieldId === item.id}
              onClick={() => onSelectField(item.id)}
              className="mx-auto mt-6 inline-flex min-w-[140px] items-center justify-center rounded-xl px-5 py-4 text-base font-bold"
              style={{
                // backgroundColor: item.buttonColor,
                backgroundColor: buttonBGColor || item.buttonColor || "#00D3F2",
                color: item.color,
              }}
            >
              {item.value}
            </EditablePreviewBox>
          );
        }

        // IMAGE
        if (item.kind === "image") {
          return (
            <EditablePreviewBox
              key={item.id}
              selected={selectedFieldId === item.id}
              onClick={() => onSelectField(item.id)}
              className="mb-4 p-2"
            >
              <img
                src={item.previewSrc || item.src || item.value}
                alt={item.label}
                className="w-full rounded-lg object-cover"
              />
            </EditablePreviewBox>
          );
        }
        // TITLE
        const isTitle =
          item.label?.toLowerCase().includes("title") ||
          item.label?.toLowerCase().includes("heading");

        // TAGLINE
        const isTag = item.label?.toLowerCase().includes("tag");

        // HIGHLIGHT
        const isHighlight = item.label?.toLowerCase().includes("highlight");

        return (
          <EditablePreviewBox
            key={item.id}
            selected={selectedFieldId === item.id}
            onClick={() => onSelectField(item.id)}
            className={`mb-3 px-3 py-3 text-center whitespace-pre-line ${
              isTitle
                ? "text-[22px] font-bold leading-[1.2]"
                : isTag
                  ? "text-[11px] tracking-[4px]"
                  : isHighlight
                    ? "text-[13px] font-semibold"
                    : "text-[13px] leading-[1.7]"
            }`}
            // style={{ color: item.color }}
            style={{
              // color: getTextColor(item, primaryColor, isTitle || isHighlight),
              color: getTextColor(item, primaryColor),
            }}
          >
            {item.value}
          </EditablePreviewBox>
        );
      })}
    </div>
  );
};

const KickoffPreview = ({ step, selectedFieldId, onSelectField }) => {
  const small = getField(step, "kickoffSmall");
  const title = getField(step, "kickoffTitle");
  const para1 = getField(step, "kickoffPara1");
  const para2 = getField(step, "kickoffPara2");
  const button = getField(step, "kickoffButton");

  return (
    <div className="w-full min-h-[560px]">
      <EditablePreviewBox
        selected={selectedFieldId === small?.id}
        onClick={() => onSelectField(small.id)}
        className="mb-3 px-3 py-3 text-center text-[11px] tracking-[4px]"
        style={{ color: small?.color }}
      >
        {small?.value}
      </EditablePreviewBox>

      <EditablePreviewBox
        selected={selectedFieldId === title?.id}
        onClick={() => onSelectField(title.id)}
        className="mb-3 px-3 py-3 text-center text-[22px] leading-[1.25]"
        style={{ color: title?.color }}
      >
        {title?.value}
      </EditablePreviewBox>

      <EditablePreviewBox
        selected={selectedFieldId === para1?.id}
        onClick={() => onSelectField(para1.id)}
        className="mb-3 px-3 py-4 text-center text-[13px] leading-[1.7]"
        style={{ color: para1?.color }}
      >
        {para1?.value}
      </EditablePreviewBox>

      <EditablePreviewBox
        selected={selectedFieldId === para2?.id}
        onClick={() => onSelectField(para2.id)}
        className="mb-16 px-3 py-4 text-center text-[13px] leading-[1.7]"
        style={{ color: para2?.color }}
      >
        {para2?.value}
      </EditablePreviewBox>

      <EditablePreviewBox
        selected={selectedFieldId === button?.id}
        onClick={() => onSelectField(button.id)}
        className="mx-auto inline-flex min-w-[160px] items-center justify-center rounded-xl px-5 py-4 text-base font-bold"
        style={{
          backgroundColor: button?.buttonColor,
          color: button?.color,
        }}
      >
        {button?.value} →
      </EditablePreviewBox>
    </div>
  );
};

const InstructionsPreview = ({
  step,
  selectedFieldId,
  onSelectField,
  // buttonBGColor,
  primaryColor,
}) => {
  const fields = [...(step.editableFields || [])].sort(
    (a, b) => a.priorityOrder - b.priorityOrder,
  );

  return (
    <div className="w-full min-h-[560px]">
      {fields.map((item, index) => {
        // BUTTON
        if (item.kind === "button") {
          return (
            <EditablePreviewBox
              key={item.id}
              selected={selectedFieldId === item.id}
              onClick={() => onSelectField(item.id)}
              className="mx-auto mt-8 inline-flex min-w-[180px] items-center justify-center rounded-xl px-5 py-4 text-base font-bold whitespace-normal break-words"
              style={{
                backgroundColor: item.buttonBGColor || primaryColor,
                // backgroundColor:
                //   item.buttonColor ||
                //   buttonBGColor ||
                //   primaryColor ||
                //   "#00D3F2",
                // backgroundColor:
                //   buttonBGColor || primaryColor || item.buttonColor,
                color: item.color,
              }}
            >
              {/* {item.value} → */}
              <span>{item.value}</span>
              {/* <ArrowRight className="ml-2 h-4 w-4" strokeWidth={3} /> */}
            </EditablePreviewBox>
          );
        }

        // IMAGE
        if (item.kind === "image") {
          return (
            <EditablePreviewBox
              key={item.id}
              selected={selectedFieldId === item.id}
              onClick={() => onSelectField(item.id)}
              className="mb-4 p-2"
            >
              <img
                src={item.src}
                alt={item.label}
                className="w-full rounded-lg object-cover"
              />
            </EditablePreviewBox>
          );
        }

        const label = item.label?.toLowerCase() || "";

        const isTitle =
          label.includes("heading") ||
          label.includes("title") ||
          label.includes("instruction");

        const isHighlight = label.includes("highlight");

        const isTip = label.includes("tip");

        // instruction pill rows
        if (isTip) {
          return (
            <InstructionPill
              key={item.id}
              field={item}
              index={index}
              selectedFieldId={selectedFieldId}
              onSelectField={onSelectField}
              primaryColor={primaryColor}
            />
          );
        }

        return (
          <EditablePreviewBox
            key={item.id}
            selected={selectedFieldId === item.id}
            onClick={() => onSelectField(item.id)}
            className={`mb-3 px-3 py-3 text-center break-words whitespace-pre-line ${
              isTitle
                ? "text-[22px] font-bold leading-[1.3]"
                : isHighlight
                  ? "text-[13px] font-semibold"
                  : "text-[13px] leading-[1.7]"
            }`}
            // style={{ color: item.color }}
            style={{
              // color: getTextColor(item, primaryColor, isTitle || isHighlight),
              color: getTextColor(item, primaryColor),
            }}
          >
            {item.value}
          </EditablePreviewBox>
        );
      })}
    </div>
  );
};

const instructionIcons = [discoverIcon, tapIcon, movesIcon];

const getInstructionIcon = (index = 0) => {
  return instructionIcons[index % instructionIcons.length];
};

const InstructionPill = ({ field, index, selectedFieldId, onSelectField }) => {
  const Icon = getInstructionIcon(index);
  return (
    <EditablePreviewBox
      selected={selectedFieldId === field?.id}
      onClick={() => field?.id && onSelectField(field.id)}
      className="mb-4 border-0 bg-transparent p-0 shadow-none"
    >
      <div
        className="
          flex items-center gap-2
          rounded-2xl
          border border-cyan-500/40
          
          px-1 py-1
          shadow-[0_0_18px_rgba(0,180,255,0.12)]
          break-words
          whitespace-normal
        "
        style={{
          background: "#1D1D21",
          border: "1px solid #353538",
        }}
      >
        {/* Icon Section */}
        <div className="flex h-10 w-10 shrink-0 items-center justify-center">
          <img
            src={Icon}
            alt="instruction icon"
            className="h-6 w-6 object-contain"
          />
        </div>

        {/* Editable Text Area */}
        <div
          className={`
            flex-1
            rounded-lg
            border-2
            bg-white/[0.02]
            px-4 py-3
            text-[15px]
            break-all
            whitespace-normal

            ${
              selectedFieldId === field?.id
                ? "border-yellow-400"
                : "border-dashed border-white/30"
            }
          `}
          style={{
            color:
              field?.usesPrimaryColor === true
                ? primaryColor || field?.color || "#ffffff"
                : field?.color || "#ffffff",
          }}
        >
          {field?.value}
        </div>
      </div>
    </EditablePreviewBox>
  );
};

const EditablePreviewBox = ({
  selected,
  onClick,
  children,
  className = "",
  style,
}) => {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`block w-full whitespace-pre-line border break-words ${
        selected ? "border-brand-23" : "border-dashed border-white/25"
      } ${className}`}
      style={style}
    >
      {children}
    </button>
  );
};

const PropertiesPanel = ({
  step,
  field,
  onChange,
  onMediaUpload,
  // buttonBGColor,
  // setButtonBGColor,
  primaryColor,
}) => {
  const selectedFieldIsImage =
    field?.kind === "image" || field?.elementType === "IMAGE";

  const selectedFieldIsVideo =
    field?.kind === "video" || field?.elementType === "VIDEO";
  const selectedFieldIsAudio =
    field?.kind === "audio" || field?.elementType === "AUDIO";

  const selectedFieldIsFile =
    field?.kind === "file" || field?.elementType === "FILE";

  const selectedFieldIsMedia =
    selectedFieldIsImage ||
    selectedFieldIsVideo ||
    selectedFieldIsAudio ||
    selectedFieldIsFile;

  const mediaUploadMeta = getMediaUploadMeta(field);
  const MediaIcon = mediaUploadMeta.Icon;
  const mediaIconSrc = mediaUploadMeta.iconSrc;
  const uploadTitle = mediaUploadMeta.title;
  const uploadHint = mediaUploadMeta.hint;
  const acceptType = mediaUploadMeta.accept;
  const mediaInputRef = useRef(null);

  const [isMediaUploading, setIsMediaUploading] = useState(false);

  const isWallScreenUploadVideoSelected =
    field?.key === WALL_SCREEN_UPLOAD_VIDEO_KEY;

  const finalMediaAccept = isWallScreenUploadVideoSelected
    ? "video/mp4,video/webm,video/ogg,video/quicktime"
    : mediaUploadMeta.accept;

  const finalUploadTitle = isWallScreenUploadVideoSelected
    ? "Upload Video"
    : uploadTitle;

  const finalUploadHint = isWallScreenUploadVideoSelected
    ? "Support formats MP4, WebM, OGG, MOV, up to 50MB"
    : uploadHint;

  const isTextSelected =
    field?.kind === "text" && field?.elementType !== "BUTTON";

  const isMediaSelected = field && isMediaField(field);
  const isInteractionStep =
    step &&
    step.id !== "instructions" &&
    !["player_interacts", "completion", "summary"].includes(
      step.id?.toLowerCase(),
    );

  if (isInteractionStep) {
    const fields = [...(step?.editableFields || [])].sort(
      (a, b) => a.priorityOrder - b.priorityOrder,
    );
    const isButton = field.kind === "button";
    const maxLength = getMaxLength(field);
    const buttonBgValue = getButtonBgColor(field, primaryColor);
    const mediaField = isMediaSelected ? field : fields.find(isMediaField);
    const mediaUrl =
      mediaField?.src ||
      mediaField?.value ||
      mediaField?.url ||
      mediaField?.mediaUrl ||
      mediaField?.fileUrl ||
      "";

    const handleMediaFileChange = async (event) => {
      const file = event.target.files?.[0];

      if (!file || !mediaField?.id || isMediaUploading) return;

      try {
        setIsMediaUploading(true);

        await onMediaUpload?.({
          file,
          fieldId: mediaField.id,
          stepId: step.id,
        });
      } finally {
        setIsMediaUploading(false);
        event.target.value = "";
      }
    };

    const allowDownloadField = fields.find((field) => {
      const id = field.id?.toLowerCase() || "";
      const label = field.label?.toLowerCase() || "";

      return id.includes("download") || label.includes("download");
    });

    return (
      <div>
        <div className="border-b border-brand-26 px-5 py-5">
          <div className="flex items-center gap-2">
            <Pointer className="h-5 w-5 text-yellow-400" />
            <p className="text-sm font-semibold">Properties</p>
          </div>

          <p className="text-sm text-neutral-100/70">
            Interaction:{" "}
            {field?.key === WALL_SCREEN_UPLOAD_VIDEO_KEY
              ? "Upload Video"
              : step?.title}
          </p>
        </div>

        <div className="space-y-6 px-5 py-5">
          {isTextSelected ? (
            <>
              <div>
                <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
                  Content
                </p>

                <label className="mb-2 block text-xs text-neutral-100/70">
                  {field.label}
                </label>

                <textarea
                  value={field.value}
                  maxLength={maxLength}
                  onChange={(event) =>
                    onChange({
                      value: event.target.value.slice(0, maxLength),
                    })
                  }
                  className="min-h-[84px] w-full rounded-md border border-transparent bg-[#1D2438] px-3 py-3 text-sm text-neutral-100 outline-none focus:border-brabg-brand-28"
                />

                <p className="mt-2 text-right text-xs text-neutral-100/45">
                  {field.value.length}/{maxLength}
                </p>
              </div>

              <div>
                <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
                  Styling
                </p>

                <label className="mb-2 block text-xs text-neutral-100/70">
                  Text Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    // value={field.color}
                    // onChange={(event) =>
                    //   onChange({ color: event.target.value })
                    // }
                    value={
                      field?.usesPrimaryColor === true
                        ? primaryColor || field.color || "#FFFFFF"
                        : field.color || "#FFFFFF"
                    }
                    onChange={(event) =>
                      onChange({
                        targetStepId: step.id,
                        targetFieldId: field.id,
                        color: event.target.value,
                        usesPrimaryColor: false,
                      })
                    }
                    className="h-9 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                  />

                  <Input
                    // value={field.color}
                    // onChange={(event) =>
                    //   onChange({ color: event.target.value })
                    // }
                    value={
                      field?.usesPrimaryColor === true
                        ? primaryColor || field.color || "#FFFFFF"
                        : field.color || "#FFFFFF"
                    }
                    onChange={(event) =>
                      onChange({
                        targetStepId: step.id,
                        targetFieldId: field.id,
                        color: event.target.value,
                        usesPrimaryColor: false,
                      })
                    }
                    className="h-9 border-none bg-[#1D2438] text-neutral-100"
                  />
                </div>
              </div>
            </>
          ) : selectedFieldIsMedia ? (
            <>
              <div>
                <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
                  MEDIA
                </p>

                {mediaUrl ? (
                  <div className="flex min-h-[150px] flex-col items-center justify-center rounded-lg bg-[#1D2438] px-4 py-6 text-center">
                    <div className="mb-8 w-full rounded-xl border border-[#3C526E] bg-[#182236] px-4 py-4 text-left text-base font-semibold text-neutral-100/80">
                      {getMediaFileName(mediaField, mediaUrl)}
                    </div>

                    <button
                      type="button"
                      disabled={isMediaUploading}
                      onClick={() => {
                        if (isMediaUploading) return;
                        mediaInputRef.current?.click();
                      }}
                      className={`rounded-md border border-[#00D3F2] px-8 py-3 text-sm font-bold text-neutral-100 ${
                        isMediaUploading ? "cursor-not-allowed opacity-60" : ""
                      }`}
                    >
                      {isMediaUploading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-100/40 border-t-neutral-100" />
                          Uploading...
                        </span>
                      ) : (
                        "Replace Media"
                      )}
                    </button>

                    <input
                      ref={mediaInputRef}
                      type="file"
                      accept={
                        isWallScreenUploadVideoSelected
                          ? "video/mp4,video/webm,video/ogg,video/quicktime"
                          : mediaUploadMeta.accept
                      }
                      className="hidden"
                      disabled={isMediaUploading}
                      onChange={handleMediaFileChange}
                    />
                  </div>
                ) : (
                  <label
                    className={`flex min-h-[150px] flex-col items-center justify-center rounded-lg border border-dashed border-[#3C526E] bg-[#1D2438] px-4 py-6 text-center hover:border-brand-28 ${
                      isMediaUploading
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer"
                    }`}
                  >
                    {/* <MediaIcon className="mb-3 h-10 w-10 text-[#00D3F2]" /> */}
                    {MediaIcon ? (
                      <MediaIcon className="mb-3 h-9 w-9 text-[#00D3F2]" />
                    ) : (
                      <img
                        src={mediaIconSrc}
                        alt={mediaUploadMeta.title}
                        className="mb-3 h-9 w-9 object-contain"
                      />
                    )}

                    <p className="text-sm font-bold text-neutral-100">
                      {isMediaUploading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-100/40 border-t-neutral-100" />
                          Uploading...
                        </span>
                      ) : isWallScreenUploadVideoSelected ? (
                        "Upload Video"
                      ) : (
                        uploadTitle
                      )}
                    </p>

                    <p className="mt-1 text-xs text-neutral-100/50">
                      {isWallScreenUploadVideoSelected
                        ? "Supported Formats : MP4, MOV. up to 50MB"
                        : uploadHint}
                    </p>

                    <input
                      type="file"
                      accept={
                        isWallScreenUploadVideoSelected
                          ? "video/mp4,video/webm,video/ogg,video/quicktime"
                          : mediaUploadMeta.accept
                      }
                      className="hidden"
                      disabled={isMediaUploading}
                      onChange={handleMediaFileChange}
                    />
                  </label>
                )}
              </div>
              {!isWallScreenUploadVideoSelected && (
                <>
                  <div className="space-y-3 rounded-lg bg-[#11182B] p-4 text-sm text-[#A7B0C3]">
                    <h4 className="font-semibold uppercase tracking-wide">
                      Note
                    </h4>

                    <ul className="list-disc space-y-3 pl-5 text-[#A7B0C3]">
                      <li>
                        For image: PNG, JPG up to 10MB, Resolution- width: 316,
                        height: 236
                      </li>

                      <li>For Video: MP4, WebM up to 50MB</li>

                      <li>For Audio: MP3, WAV up to 25MB</li>
                    </ul>
                  </div>

                  <div>
                    <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
                      Control
                    </p>

                    <label className="flex cursor-pointer gap-3 rounded-lg border border-[#344768] bg-[#1D2438] p-3">
                      <input
                        type="checkbox"
                        checked={getAllowDownloadValue(allowDownloadField)}
                        onChange={(event) => {
                          if (!allowDownloadField?.id) return;

                          onChange({
                            targetStepId: step.id,
                            targetFieldId: allowDownloadField.id,
                            value: event.target.checked,
                            checked: event.target.checked,
                          });
                        }}
                        className="mt-1 h-5 w-5 rounded border border-gray-500 bg-[#1D2438] accent-[#6998AC]"
                      />

                      <span>
                        <span className="block text-sm font-semibold text-neutral-100">
                          Allow Download
                        </span>

                        <span className="mt-1 block text-xs text-neutral-100/45">
                          Players can download media in their devices
                        </span>
                      </span>
                    </label>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div>
                <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
                  Media
                </p>

                {(() => {
                  const mediaType = getMediaType(mediaField);
                  const hasMedia = Boolean(mediaUrl);

                  const mediaMeta =
                    mediaType === "video"
                      ? {
                          Icon: VideoIcon,
                          title: hasMedia ? "Change Video" : "Upload Video",
                          hint: "MP4, WebM, OGG, MOV",
                          accept:
                            "video/mp4,video/webm,video/ogg,video/quicktime",
                        }
                      : mediaType === "audio"
                        ? {
                            Icon: Music,
                            title: hasMedia ? "Change Audio" : "Upload Audio",
                            hint: "MP3, WAV, OGG, M4A, AAC, FLAC",
                            accept: "audio/*",
                          }
                        : mediaType === "file"
                          ? {
                              Icon: FileText,
                              title: hasMedia ? "Change File" : "Upload File",
                              hint: "PDF, DOC, DOCX, XLS, XLSX, CSV, PPT, PPTX, TXT",
                              accept:
                                ".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt",
                            }
                          : mediaType === "image"
                            ? {
                                Icon: ImageIcon,
                                title: hasMedia
                                  ? "Change Image"
                                  : "Upload Image",
                                hint: "png, jpeg, jpg, webp, gif, svg",
                                accept: "image/*",
                              }
                            : {
                                Icon: ImageIcon,
                                title: hasMedia
                                  ? "Change Image"
                                  : "Upload Asset",
                                hint: "You can Upload image, video, audio, word, pdf, excel, csv, ppt and others",
                                accept: "image/*",
                              };

                  const MediaIcon = mediaMeta.Icon;

                  return (
                    <label className="flex h-[150px] cursor-pointer flex-col items-center justify-center rounded-lg bg-[#1D2438] text-center">
                      <MediaIcon className="mb-2 h-6 w-6 text-[#6998AC]" />

                      <input
                        type="file"
                        accept={mediaMeta.accept}
                        className="hidden"
                        onChange={async (event) => {
                          const file = event.target.files?.[0];

                          if (!file || !mediaField?.id) return;

                          // const localUrl = URL.createObjectURL(file);

                          const isVideoFile = file.type.startsWith("video/");
                          const isImageFile = file.type.startsWith("image/");
                          const isAudioFile = file.type.startsWith("audio/");

                          const isDocumentFile =
                            file.type.includes("pdf") ||
                            file.type.includes("word") ||
                            file.type.includes("excel") ||
                            file.type.includes("spreadsheet") ||
                            file.type.includes("presentation") ||
                            file.type.includes("powerpoint") ||
                            file.type.includes("csv") ||
                            /\.(pdf|doc|docx|xls|xlsx|csv|ppt|pptx|txt)$/i.test(
                              file.name,
                            );

                          const nextKind = isVideoFile
                            ? "video"
                            : isImageFile
                              ? "image"
                              : isAudioFile
                                ? "audio"
                                : isDocumentFile
                                  ? "file"
                                  : "file";

                          const nextElementType = isVideoFile
                            ? "VIDEO"
                            : isAudioFile
                              ? "AUDIO"
                              : isDocumentFile
                                ? "FILE"
                                : "IMAGE";

                          onChange({
                            targetStepId: step.id,
                            targetFieldId: mediaField.id,
                            src: finalUrl,
                            value: finalUrl,
                            previewSrc: `${finalUrl}?t=${Date.now()}`,
                          });

                          // await onMediaUpload?.({
                          //   file,
                          //   fieldId: mediaField.id,
                          // });

                          await onMediaUpload?.({
                            file,
                            fieldId: mediaField.id,
                            stepId: step.id,
                          });
                          event.target.value = "";
                        }}
                      />

                      <span className="text-sm font-semibold text-neutral-100">
                        {mediaMeta.title}
                      </span>

                      <span className="mt-2 max-w-[220px] text-xs text-neutral-100/45 break-words">
                        {mediaField?.fileName || mediaUrl || mediaMeta.hint}
                      </span>
                    </label>
                  );
                })()}
              </div>
              <div className="space-y-3 rounded-lg bg-[#11182B] p-4 text-sm text-[#A7B0C3]">
                <h4 className="font-semibold uppercase tracking-wide">Note</h4>

                <ul className="list-disc space-y-3 pl-5">
                  <li>
                    For image: PNG, JPG up to 10MB, Resolution- width: 316,
                    height: 236
                  </li>

                  <li>For Video: MP4, WebM up to 50MB</li>

                  <li>For Audio: MP3, WAV up to 25MB</li>
                </ul>
              </div>
              <div>
                <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
                  Control
                </p>

                <label className="flex cursor-pointer gap-3 rounded-lg border border-[#344768] bg-[#1D2438] p-3">
                  <input
                    type="checkbox"
                    checked={
                      allowDownloadField?.value === true ||
                      allowDownloadField?.value === "true" ||
                      allowDownloadField?.checked === true
                    }
                    onChange={(event) => {
                      if (!allowDownloadField?.id) return;

                      // onChange({
                      //   targetFieldId: allowDownloadField.id,
                      //   value: event.target.checked,
                      //   checked: event.target.checked,
                      // });
                      onChange({
                        targetStepId: step.id,
                        targetFieldId: allowDownloadField.id,
                        value: event.target.checked,
                        checked: event.target.checked,
                      });
                    }}
                    className="mt-1 h-5 w-5 bg-[#1D2438]"
                  />

                  <span>
                    <span className="block text-sm font-semibold text-neutral-100">
                      Allow Download
                    </span>

                    <span className="mt-1 block text-xs text-neutral-100/45">
                      Players can download media in their devices
                    </span>
                  </span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
  if (!field) {
    return (
      <div className="p-5 text-sm text-neutral-100/50">
        Select a text element to edit.
      </div>
    );
  }

  const isButton = field.kind === "button";
  const maxLength = getMaxLength(field);
  const buttonBgValue = getButtonBgColor(field, primaryColor);

  return (
    <div>
      <div className="border-b border-brand-26 px-5 py-5">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-400" />
          <p className="text-sm font-semibold">Properties</p>
        </div>

        <p className="mt-2 text-sm text-neutral-100/60">{step.title}</p>
      </div>

      <div className="space-y-6 px-5 py-5">
        <div>
          <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
            Content
          </p>

          <label className="mb-2 block text-xs text-neutral-100/70">
            {field.label}
          </label>

          <textarea
            value={field.value}
            maxLength={maxLength}
            onChange={(event) =>
              onChange({
                value: event.target.value.slice(0, maxLength),
              })
            }
            className="min-h-[84px] w-full rounded-md border border-transparent bg-[#1D2438] px-3 py-3 text-sm text-neutral-100 outline-none focus:border-brabg-brand-28"
          />

          <p className="mt-2 text-right text-xs text-neutral-100/45">
            {field.value.length}/{maxLength}
          </p>
        </div>

        <div>
          <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
            Styling
          </p>

          <label className="mb-2 block text-xs text-neutral-100/70">
            Text Color
          </label>

          <div className="flex items-center gap-3">
            <input
              type="color"
              value={
                field?.usesPrimaryColor === true
                  ? primaryColor || field.color || "#FFFFFF"
                  : field.color || "#FFFFFF"
              }
              onChange={(event) =>
                onChange({
                  targetStepId: step.id,
                  targetFieldId: field.id,
                  color: event.target.value,
                  usesPrimaryColor: false,
                })
              }
              className="h-9 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
            />

            <Input
              value={
                field?.usesPrimaryColor === true
                  ? primaryColor || field.color || "#FFFFFF"
                  : field.color || "#FFFFFF"
              }
              onChange={(event) =>
                onChange({
                  targetStepId: step.id,
                  targetFieldId: field.id,
                  color: event.target.value,
                  usesPrimaryColor: false,
                })
              }
              className="h-9 border-none bg-[#1D2438] text-neutral-100"
            />
          </div>

          {isButton && (
            <div className="mt-5">
              <label className="mb-2 block text-xs text-neutral-100/70">
                Button Color
              </label>

              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={buttonBgValue}
                  onChange={(event) => {
                    const nextColor = event.target.value;

                    // setButtonBGColor(nextColor);

                    onChange({
                      targetStepId: step.id,
                      targetFieldId: field.id,
                      buttonBGColor: nextColor,
                    });
                  }}
                  className="h-9 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                />

                <Input
                  value={buttonBgValue}
                  onChange={(event) => {
                    const nextColor = event.target.value;

                    // setButtonBGColor(nextColor);

                    onChange({
                      targetStepId: step.id,
                      targetFieldId: field.id,
                      buttonBGColor: nextColor,
                    });
                  }}
                  className="h-9 border-none bg-[#1D2438] text-neutral-100"
                />
              </div>

              <p className="mt-2 text-xs text-neutral-100/45">
                Button text max 20 characters
              </p>
            </div>
          )}

          {/* {isButton && (
            <div className="mt-5">
              <label className="mb-2 block text-xs text-neutral-100/70">
                Button Color
              </label>

              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={buttonBGColor || "#00D3F2"}
                  value={
                    field?.usesPrimaryColor === true
                      ? primaryColor || buttonBGColor || "#FFFFFF"
                      : buttonBGColor || "#FFFFFF"
                  }
                  onChange={(event) => setButtonBGColor(event.target.value)}
                  className="h-9 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                />

                <Input
                  value={field.buttonColor}
                  // onChange={(event) =>
                  //   onChange({ buttonColor: event.target.value })
                  // }
                  onChange={(event) => {
                    const nextColor = event.target.value;

                    setButtonBGColor(nextColor);
                    setIsDirty(true);

                    onChange({
                      targetStepId: step.id,
                      targetFieldId: field.id,
                      buttonColor: nextColor,
                    });
                  }}
                  className="h-9 border-none bg-[#1D2438] text-neutral-100"
                />
              </div>

              <p className="mt-2 text-xs text-neutral-100/45">
                Button text max 20 characters
              </p>
            </div>
          )} */}

          {!isButton && (
            <p className="mt-4 text-xs text-neutral-100/45">
              Text max 120 characters
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
